// PreventivoModal.jsx
// Form preventivo a 4 step + generazione PDF con jsPDF

import { useState, useMemo, useEffect, useRef } from "react";
import { ref, set, get, getDatabase } from "firebase/database";

const fmt  = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

// ─── STEP INDICATOR ───────────────────────────────────────────────────────────
function StepBar({ step, isMobile, isDark }) {
  const steps = ['Veicolo', 'Prezzo e Servizi', 'Cliente'];
  const active = isDark ? '#3b82f6' : '#2563eb';
  const done = '#10b981';
  const inactive = isDark ? '#1e2230' : '#e2e8f0';
  const inactiveText = isDark ? '#475569' : '#94a3b8';
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', gap: 0 }}>
      {steps.map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: i < step ? done : i === step ? active : inactive, border: `2px solid ${i <= step ? (i < step ? done : active) : inactiveText}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '900', color: '#fff', flexShrink: 0 }}>
              {i < step ? '✓' : i + 1}
            </div>
            {!isMobile && <div style={{ fontSize: '9px', fontWeight: '700', color: i === step ? (isDark ? '#fff' : '#0f172a') : inactiveText, marginTop: '4px', whiteSpace: 'nowrap' }}>{s}</div>}
          </div>
          {i < steps.length - 1 && <div style={{ height: '2px', flex: 1, background: i < step ? done : inactive, margin: '0 4px', marginBottom: isMobile ? 0 : 16 }} />}
        </div>
      ))}
    </div>
  );
}

// ─── FIELD ────────────────────────────────────────────────────────────────────
function Field({ label, children, half, third }) {
  const isDark = (() => { try { return (localStorage.getItem('radar_theme') || 'dark') === 'dark'; } catch { return true; } })();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: third ? '0 0 calc(33% - 8px)' : half ? '0 0 calc(50% - 8px)' : '1 1 100%', minWidth: 0 }}>
      <label style={{ fontSize: '9px', fontWeight: '900', color: isDark ? '#64748b' : '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</label>
      {children}
    </div>
  );
}

const _isDark = () => { try { return (localStorage.getItem('radar_theme') || 'dark') === 'dark'; } catch { return true; } };
const inputStyle = { background: _isDark() ? '#0a0c10' : '#f8fafc', border: `1px solid ${_isDark() ? '#2d334a' : '#cbd5e1'}`, borderRadius: '10px', padding: '10px 12px', color: _isDark() ? '#fff' : '#0f172a', fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box' };
const inputStyleReq = { ...inputStyle, border: '1px solid #ef4444' };

// Su Usato/Km0 questa voce non è "IPT + Messa su Strada" (che ha senso solo
// sul Nuovo) ma "Passaggio di Proprietà / Immatricolazione Italiana".
const labelIPT = tipo => (tipo || '').toLowerCase().trim() === 'nuovo' ? 'IPT + Messa su Strada' : 'Passaggio di Proprietà / Immatricolazione Italiana';

// Tabella prezzi Service Care Gruppo Autoscala — pacchetto manutenzione
// (Olio Motore, Filtro Olio, Filtro Aria, Filtro Abitacolo inclusi sempre).
// Prezzo dipende da categoria (derivata da cilindrata+alimentazione) e
// durata (36/48 mesi).
const TABELLA_SERVICE_CARE = {
  piccolo: { benzina: { 36: 599, 48: 749 }, diesel: { 36: 749, 48: 899 } },
  medio:   { benzina: { 36: 699, 48: 849 }, diesel: { 36: 849, 48: 999 } },
  grande:  { benzina: { 36: 1049, 48: 1249 }, diesel: { 36: 1199, 48: 1399 } },
};
// Restituisce il prezzo suggerito, o null se mancano i dati per calcolarlo
// (cilindrata sconosciuta, o durata non tra le due previste).
// Estrae la cilindrata da un testo libero (versione/allestimento/descrizione)
// quando il campo dedicato manca — riconosce sia "1200cc"/"1200 cc" sia
// "1.2"/"1,2" (litri, tipico inizio di una versione auto: "1.2 PureTech").
function estraiCilindrataTesto(testo) {
  if (!testo) return null;
  const t = testo.toString();
  let m = t.match(/(\d{3,4})\s*cc\b/i);
  if (m) return parseInt(m[1]);
  m = t.match(/\b([0-6])[.,](\d)\b/);
  if (m) return Math.round(parseFloat(`${m[1]}.${m[2]}`) * 1000);
  return null;
}

function calcolaPrezzoServiceCare(cc, carburante, mesi) {
  const ccNum = parseFloat(cc) || 0;
  const m = parseInt(mesi);
  if (!ccNum || (m !== 36 && m !== 48)) return null;
  const car = (carburante || '').toLowerCase();
  const isDiesel = car.includes('diesel') || car.includes('gasolio');

  let categoria;
  if (isDiesel) {
    categoria = ccNum <= 1600 ? 'piccolo' : ccNum <= 2200 ? 'medio' : 'grande';
  } else {
    categoria = ccNum <= 1400 ? 'piccolo' : ccNum <= 2000 ? 'medio' : 'grande';
  }
  return TABELLA_SERVICE_CARE[categoria]?.[isDiesel ? 'diesel' : 'benzina']?.[m] ?? null;
}

// Società finanziarie standard, con normalizzazione delle scritture simili
// (es. "Deutsche", "Deutche Bank", "Deutsch Bank" → "DEUTSCHE BANK").
const FINANZIARIE_OPTIONS = ['COMPASS', 'DEUTSCHE BANK', 'CA BANK', 'FINDOMESTIC', 'AGOS', 'BCC', 'SANTANDER', 'MOBILIZE'];
const FINANZIARIE_MAPPE = [
  { canonico: 'COMPASS', chiavi: ['compass'] },
  { canonico: 'DEUTSCHE BANK', chiavi: ['deutsche', 'deutch', 'deutsch'] },
  { canonico: 'CA BANK', chiavi: ['ca bank', 'credit agricole', 'credito agricole', 'agricole'] },
  { canonico: 'FINDOMESTIC', chiavi: ['findomestic', 'find omestic'] },
  { canonico: 'AGOS', chiavi: ['agos'] },
  { canonico: 'BCC', chiavi: ['bcc'] },
  { canonico: 'SANTANDER', chiavi: ['santander'] },
  { canonico: 'MOBILIZE', chiavi: ['mobilize', 'moobilize', 'rci banque', 'rci bank'] },
];
// Restituisce il nome canonico se riconosce una variante nota, altrimenti
// null (da trattare come testo libero/"Altro").
function normalizzaFinanziaria(testo) {
  const t = (testo || '').toLowerCase().trim();
  if (!t) return null;
  const trovata = FINANZIARIE_MAPPE.find(m => m.chiavi.some(k => t.includes(k)));
  return trovata ? trovata.canonico : null;
}
const selectStyle = { ...inputStyle, cursor: 'pointer' };

// ─── STEP 1: VEICOLO ─────────────────────────────────────────────────────────
function Step1Veicolo({ data, onChange, user }) {
  const ruolo = (user?.ruolo || user?.Ruolo || '').toLowerCase().trim();
  const puoModListino = ['admin','super_admin','consulente','brand_manager_se','back_office','finance','bdc','logistica'].includes(ruolo);
  const isAdminModListino = ruolo === 'admin' || ruolo === 'super_admin';
  const isOffStock = data.offStock === true;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
      {isOffStock ? (
        <div style={{ width: '100%', borderRadius: '12px', padding: '16px', border: '2px solid #8b5cf6', background: '#0a0c10' }}>
          <div style={{ fontSize: '9px', color: '#8b5cf6', fontWeight: '900', marginBottom: '12px', letterSpacing: '1px' }}>🚗 PREVENTIVO OFF STOCK — Inserisci i dati del veicolo</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {[['Marca','marca'],['Modello','modello'],['Targa','targa'],['Telaio (VIN)','vin'],['Colore','coloreVeicolo'],['Versione','versione'],['Immatricolazione (mm/aaaa)','registrazione'],['Km','km'],['Carburante','carburante']].map(([label,key])=>(
              <div key={key}>
                <div style={{ fontSize: '9px', color: '#9ca3af', marginBottom: '4px' }}>{label}</div>
                <input
                  type={key === 'km' ? 'number' : 'text'}
                  value={(key === 'registrazione' ? (data.registrazione || data.anno) : data[key]) || ''}
                  onChange={e => { e.stopPropagation(); onChange(key, e.target.value); }}
                  placeholder={label}
                  style={{ background: '#1e2230', border: '1px solid #3d4563', borderRadius: '8px', padding: '9px 12px', color: '#fff', fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box', pointerEvents: 'auto', userSelect: 'text' }}
                />
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: '9px', color: '#9ca3af', marginBottom: '4px' }}>Prezzo Listino (€)</div>
              <input
                type="number"
                value={data.prezzo||''}
                onChange={e => { e.stopPropagation(); onChange('prezzo', parseFloat(e.target.value)||0); }}
                onWheel={e=>e.preventDefault()}
                placeholder="0.00"
                style={{ background: '#1e2230', border: '1px solid #8b5cf6', borderRadius: '8px', padding: '9px 12px', color: '#a78bfa', fontSize: '14px', fontWeight: '700', outline: 'none', width: '100%', boxSizing: 'border-box', pointerEvents: 'auto', userSelect: 'text' }}
              />
            </div>
          </div>
        </div>
      ) : (
      <div style={{ width: '100%', background: '#0a0c10', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230', display: 'flex', gap: '16px', alignItems: 'center' }}>
        {data.mainImg && <img src={data.mainImg} alt="" style={{ width: '100px', height: '70px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />}
        <div>
          <div style={{ fontSize: '9px', color: '#3b82f6', fontWeight: '900', marginBottom: '4px' }}>{data.tipo} · {data.classe?.toUpperCase()}</div>
          <div style={{ fontSize: '18px', fontWeight: '900', color: '#fff' }}>{data.marca} {data.modello}</div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{data.versione}</div>
          <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '11px', color: '#9ca3af' }}>
            <span>{new Intl.NumberFormat('it-IT').format(data.km)} km</span>
            <span>{data.carburante}</span>
            <span>Targa: {data.targa || 'N.D.'}</span>
            <span style={{ color: '#10b981', fontWeight: '700' }}>{fmt(data.prezzo)}</span>
          </div>
        </div>
      </div>
      )}
      <Field label="Prezzo Listino Chiavi in Mano (€)">
        <div style={{ position: 'relative' }}>
          <input type="number" value={data.prezzoListino || data.prezzo || ''}
            onChange={e => puoModListino && onChange('prezzoListino', parseFloat(e.target.value) || 0)}
            readOnly={!puoModListino}
            onWheel={e => e.preventDefault()}
            style={{
              ...inputStyle,
              background: puoModListino ? 'rgba(16,185,129,0.08)' : 'rgba(55,65,81,0.3)',
              border: puoModListino ? '1px solid #10b981' : '1px solid #374151',
              color: puoModListino ? '#065f46' : undefined,
              fontSize: '16px',
              fontWeight: '800',
              cursor: !puoModListino ? 'not-allowed' : undefined,
              paddingRight: '36px',
            }} />
          <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: '#10b981', fontWeight: '700', pointerEvents: 'none' }}>€</span>
        </div>
        {!puoModListino && <div style={{ fontSize: '9px', color: '#f59e0b', marginTop: '3px' }}>🔒 Solo admin e back office possono modificare il prezzo listino</div>}
      </Field>
      <Field label="Opzioni / Pacchetti">
        <textarea value={data.opzioni || ''} onChange={e => onChange('opzioni', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Descrizione opzioni e costi..." />
      </Field>
      <Field label="Data Consegna Prevista" half>
        <input type="date" value={data.dataConsegna || ''} onChange={e => onChange('dataConsegna', e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Ubicazione" half>
        <input type="text" value={data.ubicazione || data.sede || ''} onChange={e => onChange('ubicazione', e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Colore">
        <input type="text" value={data.coloreVeicolo || data.colore || ''} onChange={e => onChange('coloreVeicolo', e.target.value)} style={inputStyle} />
      </Field>

      {/* LEGGE 104 */}
      <div style={{ width: '100%', background: '#0a0c10', borderRadius: '12px', padding: '14px 16px', border: `1px solid ${data.legge104 ? '#3b82f6' : '#1e2230'}`, transition: 'border-color 0.2s' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={data.legge104 || false}
            onChange={e => onChange('legge104', e.target.checked)}
            style={{ accentColor: '#3b82f6', width: 18, height: 18, flexShrink: 0 }}
          />
          <div>
            <div style={{ fontSize: '12px', fontWeight: '900', color: data.legge104 ? '#3b82f6' : '#9ca3af' }}>
              Acquisto con Legge 104
            </div>
            <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
              IVA agevolata 4% — il prezzo listino verrà ricalcolato automaticamente
            </div>
          </div>
          {data.legge104 && data.prezzoListino > 0 && (
            <div style={{ marginLeft: 'auto', textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: '9px', color: '#64748b', fontWeight: '700' }}>PREZZO AGEVOLATO</div>
              <div style={{ fontSize: '15px', fontWeight: '900', color: '#3b82f6' }}>
                {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round((parseFloat(data.prezzoListino) / 1.22) * 1.04))}
              </div>
              <div style={{ fontSize: '9px', color: '#10b981', fontWeight: '700' }}>
                Risparmio: {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(parseFloat(data.prezzoListino) - (parseFloat(data.prezzoListino) / 1.22) * 1.04))}
              </div>
            </div>
          )}
        </label>
      </div>
    </div>
  );
}

// ─── STEP 2: CLIENTE ──────────────────────────────────────────────────────────
function Step2Cliente({ data, onChange, preventiviEsistenti, isDark }) {
  const bg = isDark ? '#0a0c10' : '#f8fafc';
  const border = isDark ? '#1e2230' : '#e2e8f0';
  const textColor = isDark ? '#fff' : '#0f172a';
  const subColor = isDark ? '#64748b' : '#475569';
  const iStyle = { background: bg, border: `1px solid ${isDark ? '#2d334a' : '#cbd5e1'}`, borderRadius: '10px', padding: '10px 12px', color: textColor, fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box' };
  const [search, setSearch] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);

  // Estrai clienti unici dai preventivi esistenti — ordinati per data
  // decrescente PRIMA di costruire la mappa, così per ogni cliente teniamo
  // sempre i suoi dati più recenti, e la lista risultante ha già i clienti
  // più recenti in cima (stessa correzione applicata anche in App.jsx).
  const clientiEsistenti = useMemo(() => {
    if (!preventiviEsistenti?.length) return [];
    // Stesso fallback data usato nella lista principale (_ts già calcolato
    // lì se disponibile: dataCreazione se presente, altrimenti data).
    const ordinati = [...preventiviEsistenti].sort((a, b) => (b._ts || new Date(b.dataCreazione || b.data || 0).getTime()) - (a._ts || new Date(a.dataCreazione || a.data || 0).getTime()));
    const map = {};
    ordinati.forEach(p => {
      const c = p.cliente;
      if (!c) return;
      const key = c.codiceFiscale || c.piva || c.email || `${c.nome}${c.cognome}`;
      if (!key) return;
      if (!map[key]) map[key] = c;
    });
    return Object.values(map);
  }, [preventiviEsistenti]);

  const suggeriti = useMemo(() => {
    if (!search.trim()) return clientiEsistenti.slice(0, 8);
    const s = search.toLowerCase();
    return clientiEsistenti.filter(c =>
      (c.ragioneSociale||'').toLowerCase().includes(s) ||
      (`${c.nome||''} ${c.cognome||''}`).toLowerCase().includes(s) ||
      (c.codiceFiscale||'').toLowerCase().includes(s) ||
      (c.cellulare||'').includes(s)
    ).slice(0, 8);
  }, [search, clientiEsistenti]);

  const selezionaCliente = (c) => {
    Object.entries(c).forEach(([k, v]) => onChange(k, v));
    setShowSuggest(false);
    setSearch('');
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
      {/* Ricerca cliente esistente */}
      {clientiEsistenti.length > 0 && (
        <div style={{ width: '100%', position: 'relative' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: '#3b82f6', marginBottom: '4px' }}>CLIENTE ESISTENTE</div>
          <input
            placeholder={`🔍 Cerca tra ${clientiEsistenti.length} clienti già censiti...`}
            value={search}
            onChange={e => { setSearch(e.target.value); setShowSuggest(true); }}
            onFocus={() => setShowSuggest(true)}
            onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
            style={iStyle}
          />
          {showSuggest && suggeriti.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: isDark ? '#0f1117' : '#ffffff', border: `1px solid ${border}`, borderRadius: '12px', zIndex: 500, maxHeight: '240px', overflowY: 'auto', marginTop: '4px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
              {suggeriti.map((c, i) => {
                const nome = c.ragioneSociale || `${c.nome||''} ${c.cognome||''}`.trim();
                return (
                  <div key={i} onClick={() => selezionaCliente(c)}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = isDark ? '#1e2230' : '#f1f5f9'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: textColor }}>{nome}</div>
                      <div style={{ fontSize: '10px', color: subColor }}>{c.codiceFiscale || c.piva || ''} {c.cellulare ? `· ${c.cellulare}` : ''}</div>
                    </div>
                    <span style={{ fontSize: '10px', color: '#3b82f6', fontWeight: '700', flexShrink: 0 }}>Seleziona →</span>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ height: '1px', background: border, margin: '12px 0 4px' }} />
          <div style={{ fontSize: '9px', color: subColor, fontWeight: '700' }}>O COMPILA MANUALMENTE</div>
        </div>
      )}

      <Field label="Nome" half required>
        <input type="text" value={data.nome || ''} onChange={e => onChange('nome', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Cognome" half required>
        <input type="text" value={data.cognome || ''} onChange={e => onChange('cognome', e.target.value)} style={iStyle} />
      </Field>

      {/* B2B FLAG */}
      <div style={{ width: '100%', marginBottom: '10px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: data.b2b ? 'rgba(99,102,241,0.12)' : (isDark ? '#0f172a' : '#f1f5f9'), border: `2px solid ${data.b2b ? '#6366f1' : (isDark ? '#1e2230' : '#e2e8f0')}`, borderRadius: '12px', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!data.b2b} onChange={e => onChange('b2b', e.target.checked || null)}
            style={{ accentColor: '#6366f1', width: '16px', height: '16px', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: '12px', fontWeight: '900', color: data.b2b ? '#6366f1' : textColor }}>
              🏢 Cliente B2B
            </div>
            <div style={{ fontSize: '10px', color: subColor, marginTop: '1px' }}>
              Rivenditore auto / Concessionaria / Operatore del settore
            </div>
          </div>
        </label>
      </div>

      {/* PROVENIENZA — obbligatoria solo per B2C (i clienti B2B arrivano
          quasi sempre da rapporti commerciali diretti, non da un canale di
          marketing tracciabile in questo modo). */}
      {!data.b2b && (
        <Field label="Provenienza *">
          <select value={data.provenienza || ''} onChange={e => onChange('provenienza', e.target.value)}
            style={{ ...iStyle, cursor: 'pointer', border: `1px solid ${!data.provenienza ? '#ef4444' : (isDark ? '#2d334a' : '#cbd5e1')}` }}>
            <option value="">Seleziona provenienza...</option>
            <option value="Autoscout">Autoscout</option>
            <option value="Google Ads">Google Ads</option>
            <option value="Facebook/Instagram">Facebook/Instagram</option>
            <option value="TikTok">TikTok</option>
            <option value="Sito Web">Sito Web</option>
            <option value="Ingresso">Ingresso</option>
            <option value="Telefonata">Telefonata</option>
            <option value="Affissioni">Affissioni</option>
            <option value="Il Bello del Calcio">Il Bello del Calcio</option>
            <option value="Giochiamo d'Anticipo">Giochiamo d'Anticipo</option>
            <option value="L'infedele">L'infedele</option>
            <option value="Radio">Radio</option>
            <option value="Evento">Evento</option>
            <option value="Altro">Altro</option>
          </select>
          {!data.provenienza && <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '4px' }}>Campo obbligatorio per i clienti privati</div>}
        </Field>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: data.giaCliente ? 'rgba(16,185,129,0.12)' : (isDark ? '#0f172a' : '#f1f5f9'), border: `2px solid ${data.giaCliente ? '#10b981' : (isDark ? '#1e2230' : '#e2e8f0')}`, borderRadius: '12px', cursor: 'pointer', marginBottom: '12px' }}>
        <input type="checkbox" checked={!!data.giaCliente} onChange={e => onChange('giaCliente', e.target.checked || null)}
          style={{ width: '18px', height: '18px', accentColor: '#10b981' }} />
        <div style={{ fontSize: '12px', fontWeight: '900', color: data.giaCliente ? '#10b981' : textColor }}>
          🔁 Già Cliente
        </div>
      </label>
      <Field label="Ragione Sociale (se azienda)">
        <input type="text" value={data.ragioneSociale || ''} onChange={e => onChange('ragioneSociale', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Nato a" half>
        <input type="text" value={data.natoA || ''} onChange={e => onChange('natoA', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Data di Nascita" half>
        <input type="date" value={data.dataNascita || ''} onChange={e => onChange('dataNascita', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Codice Fiscale" half>
        <input type="text" value={data.codiceFiscale || ''} onChange={e => onChange('codiceFiscale', e.target.value)} style={{ ...iStyle, textTransform: 'uppercase' }} />
      </Field>
      <Field label="P.IVA" half>
        <input type="text" value={data.piva || ''} onChange={e => onChange('piva', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Professione / Attività">
        <input type="text" value={data.professione || ''} onChange={e => onChange('professione', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Indirizzo Residenza">
        <input type="text" value={data.indirizzo || ''} onChange={e => onChange('indirizzo', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Città" third required>
        <input type="text" value={data.citta || ''} onChange={e => onChange('citta', e.target.value)} style={iStyle} />
      </Field>
      <Field label="CAP" third required>
        <input type="text" value={data.cap || ''} onChange={e => onChange('cap', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Prov." third>
        <input type="text" value={data.provincia || ''} onChange={e => onChange('provincia', e.target.value)} style={{ ...iStyle, textTransform: 'uppercase' }} maxLength={2} />
      </Field>
      <Field label="Cellulare" half required>
        <input type="tel" value={data.cellulare || ''} onChange={e => onChange('cellulare', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Cellulare 2" half>
        <input type="tel" value={data.cellulare2 || ''} onChange={e => onChange('cellulare2', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Email" half required>
        <input type="email" value={data.email || ''} onChange={e => onChange('email', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Email 2" half>
        <input type="email" value={data.email2 || ''} onChange={e => onChange('email2', e.target.value)} style={iStyle} />
      </Field>
      <Field label="PEC">
        <input type="email" value={data.pec || ''} onChange={e => onChange('pec', e.target.value)} style={iStyle} />
      </Field>
      <Field label="Codice SDI / Univoco">
        <input type="text" value={data.sdi || ''} onChange={e => onChange('sdi', e.target.value)} style={iStyle} />
      </Field>
    </div>
  );
}

// ─── STEP 3: PREZZO ───────────────────────────────────────────────────────────
function Step3Prezzo({ data, onChange, veicolo, legge104, totaleServizi, accessoriCMTot: accessoriCMTotProp, user }) {
  const _isPartner = (user?.ruolo||'').toLowerCase() === 'partner';
  const ruolo = (user?.ruolo || user?.Ruolo || '').toLowerCase().trim();
  // Admin e super_admin: modifica libera (aumento e diminuzione)
  // Consulente/back_office/altri: possono SOLO aumentare rispetto al prezzo originale del veicolo
  const isAdminListino = ruolo === 'admin' || ruolo === 'super_admin';
  const isConsulenteListino = ['consulente','back_office','finance','bdc'].includes(ruolo);
  const puoModificareListino = isAdminListino || isConsulenteListino;
  const prezzoVeicoloDefault = _isPartner && veicolo.prezzoPartner > 0 ? veicolo.prezzoPartner : veicolo.prezzo;
  const prezzoOriginaleVeicolo = parseFloat(prezzoVeicoloDefault || 0); // prezzo minimo invalicabile per non-admin
  const prezzoBase = parseFloat(data.prezzoListino || prezzoVeicoloDefault || 0);
  const prezzoListino = legge104 ? Math.round((prezzoBase / 1.22) * 1.04) : prezzoBase;
  const sconto = parseFloat(data.sconto || 0);
  const prezzoPattuito = prezzoListino - sconto;
  const ipt = parseFloat(data.ipt || 0);
  const accessoriCMTot = accessoriCMTotProp || 0;
  const permuta = valorePermuteTot(data);
  const caparra = parseFloat(data.caparra || 0);
  const prezzoFinale = prezzoPattuito + ipt + accessoriCMTot;
  const importoFinanziato = parseFloat(data.importoFinanziato || 0);
  const totS = totaleServizi || 0;
  const hasIVA = !!data.hasIVA;
  const ivaImporto = hasIVA ? Math.round(prezzoPattuito * 0.22) : 0;
  const estinzioneCosto = data.estinzioneAnticipata ? parseFloat(data.costoEstinzioneAnticipata || 0) : 0;
  const speseLeasingCosto = data.speseGestioneLeasing ? parseFloat(data.costoSpeseGestioneLeasing || 0) : 0;
  const saldo = prezzoFinale + totS + ivaImporto - permuta - caparra - importoFinanziato + estinzioneCosto + speseLeasingCosto;

  // Sync calculated values
  const handleChange = (field, value) => {
    onChange(field, value);
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
      {/* RIEPILOGO PREZZI */}
      <div style={{ width: '100%', background: '#0a0c10', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: '#3b82f6', marginBottom: '12px', letterSpacing: '1px' }}>RIEPILOGO PREZZI</div>
        {[
          ['Prezzo Listino', fmt(prezzoListino), '#9ca3af'],
          ['Sconto Discrezionale', '- ' + fmt(sconto), '#ef4444'],
          ['Prezzo Pattuito', fmt(prezzoPattuito), '#fff'],
          hasIVA && ['IVA 22%', '+ ' + fmt(ivaImporto), '#f59e0b'],
          [labelIPT(veicolo?.tipo), '+ ' + fmt(ipt), '#9ca3af'],
          ...(accessoriCMTot > 0 ? [['Optional Casa Madre', '+ ' + fmt(accessoriCMTot), '#9ca3af']] : []),
          totS > 0 && ['Servizi e Accessori', '+ ' + fmt(totS), '#8b5cf6'],
          estinzioneCosto > 0 && ['Estinzione Anticipata Finanziamento', '+ ' + fmt(estinzioneCosto), '#8b5cf6'],
          speseLeasingCosto > 0 && ['Spese Gestione Leasing', '+ ' + fmt(speseLeasingCosto), '#8b5cf6'],
          ['Prezzo Concordato', fmt(prezzoFinale + totS + ivaImporto), '#3b82f6'],
          permuta > 0 && [data.permutaRottamazione ? 'Rottamazione' : 'Permuta Usato', '- ' + fmt(permuta), '#f59e0b'],
          importoFinanziato > 0 && ['Importo Finanziato', '- ' + fmt(importoFinanziato), '#8b5cf6'],
          caparra > 0 && ['Caparra Confirmatoria', '- ' + fmt(caparra), '#ec4899'],
          ['SALDO', fmt(saldo), '#10b981'],
        ].filter(Boolean).map(([l, v, c]) => (
          <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${_isDark() ? '#1e2230' : '#e2e8f0'}`, fontSize: '12px' }}>
            <span style={{ color: '#9ca3af' }}>{l}</span>
            <b style={{ color: c }}>{v}</b>
          </div>
        ))}
      </div>

      <Field label={legge104 ? "Prezzo Agevolato Legge 104 (€) — calcolato auto" : _isPartner ? "Prezzo Partner (€)" : "Prezzo Listino Chiavi in Mano (€)"} half>
        <div style={{ position: 'relative' }}>
          <input type="number"
            value={legge104
              ? Math.round((parseFloat(data.prezzoListino || veicolo.prezzo || 0) / 1.22) * 1.04)
              : (data.prezzoListino || (_isPartner && veicolo.prezzoPartner > 0 ? veicolo.prezzoPartner : veicolo.prezzo) || '')}
            onChange={e => {
              if (!puoModificareListino || legge104) return;
              const nuovoVal = parseFloat(e.target.value) || 0;
              // Consulenti: possono solo aumentare il listino, mai scendere sotto il prezzo originale
              if (!isAdminListino && nuovoVal < prezzoOriginaleVeicolo) {
                e.target.value = prezzoOriginaleVeicolo; // ripristina
                return;
              }
              handleChange('prezzoListino', nuovoVal);
            }}
            onBlur={e => {
              if (!puoModificareListino || legge104 || isAdminListino) return;
              const nuovoVal = parseFloat(e.target.value) || 0;
              if (nuovoVal < prezzoOriginaleVeicolo) {
                e.target.value = prezzoOriginaleVeicolo;
                handleChange('prezzoListino', prezzoOriginaleVeicolo);
              }
            }}
            readOnly={legge104 || !puoModificareListino}
            onWheel={e => e.preventDefault()}
            style={{
              ...inputStyle,
              background: (puoModificareListino && !legge104) ? (isAdminListino ? 'rgba(16,185,129,0.08)' : 'rgba(99,102,241,0.08)') : 'rgba(55,65,81,0.3)',
              border: legge104 ? '1px solid #3b82f6' : _isPartner ? '1px solid #f59e0b' : isAdminListino ? '1px solid #10b981' : isConsulenteListino ? '1px solid #6366f1' : '1px solid #374151',
              color: legge104 ? '#1e40af' : _isPartner ? '#92400e' : isAdminListino ? '#065f46' : isConsulenteListino ? '#4338ca' : undefined,
              fontSize: '16px',
              fontWeight: '800',
              cursor: (!puoModificareListino && !legge104) ? 'not-allowed' : undefined,
              paddingRight: '36px',
            }} />
          <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: legge104 ? '#3b82f6' : _isPartner ? '#f59e0b' : '#10b981', fontWeight: '700', pointerEvents: 'none' }}>€</span>
        </div>
        {!puoModificareListino && !legge104 && (
          <div style={{ fontSize: '9px', color: '#f59e0b', marginTop: '3px' }}>🔒 Solo admin può modificare il prezzo listino</div>
        )}
        {isConsulenteListino && !legge104 && (
          <div style={{ fontSize: '9px', color: '#6366f1', marginTop: '3px' }}>↑ Puoi aumentare il listino, non diminuirlo (min. {new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(prezzoOriginaleVeicolo)})</div>
        )}
      </Field>
      {legge104 && (
        <div style={{ width: '100%', padding: '8px 12px', background: 'rgba(59,130,246,0.08)', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.2)', fontSize: '10px', color: '#3b82f6', fontWeight: '700' }}>
          ℹ️ Legge 104 attiva — IVA 4% applicata. Prezzo originale: {new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(parseFloat(data.prezzoListino||veicolo.prezzo||0))}
        </div>
      )}
      <Field label="Sconto Discrezionale (€)" half>
        <input type="number" value={data.sconto || ''} onChange={e => handleChange('sconto', e.target.value)} style={inputStyle} placeholder="0" />
      </Field>
      <Field label={`${labelIPT(veicolo?.tipo)} (€)`} half>
        <input type="number" value={data.ipt || ''} onChange={e => handleChange('ipt', e.target.value)} style={inputStyle} placeholder="0" />
      </Field>
      <Field label="IVA" half>
        <button
          onClick={() => handleChange('hasIVA', !data.hasIVA)}
          style={{
            width: '100%', padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
            fontWeight: '900', fontSize: '12px', letterSpacing: '0.5px',
            background: data.hasIVA ? 'rgba(245,158,11,0.15)' : 'transparent',
            border: data.hasIVA ? '2px solid #f59e0b' : '2px solid #374151',
            color: data.hasIVA ? '#f59e0b' : '#9ca3af',
            transition: 'all 0.2s',
          }}>
          {data.hasIVA
            ? `✓ IVA 22% — + ${new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(ivaImporto)}`
            : '+ AGGIUNGI IVA 22%'}
        </button>
      </Field>
      <Field label="Modalità di Pagamento" half>
        <select value={data.modalitaPagamento || ''} onChange={e => handleChange('modalitaPagamento', e.target.value)} style={selectStyle}>
          <option value="">Seleziona...</option>
          <option value="Contanti">Contanti</option>
          <option value="Bonifico">Bonifico Bancario</option>
          <option value="Finanziamento">Finanziamento</option>
          <option value="Leasing">Leasing</option>
          <option value="Misto">Misto</option>
        </select>
      </Field>

      {/* PERMUTA */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <div style={{ fontSize: '10px', fontWeight: '900', color: '#f59e0b', letterSpacing: '1px' }}>RITIRO VEICOLO USATO</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input type="checkbox" checked={data.hasPermuta || false} onChange={e => handleChange('hasPermuta', e.target.checked)} style={{ accentColor: '#f59e0b' }} />
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Presente</span>
          </label>
          {data.hasPermuta && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginLeft: '10px' }} title="Il veicolo va rottamato, non rientra nel circuito di permuta/rivendita">
              <input type="checkbox" checked={data.permutaRottamazione || false} onChange={e => handleChange('permutaRottamazione', e.target.checked)} style={{ accentColor: '#ef4444' }} />
              <span style={{ fontSize: '11px', color: data.permutaRottamazione ? '#ef4444' : '#9ca3af', fontWeight: data.permutaRottamazione ? '800' : '400' }}>🗑️ Rottamazione</span>
            </label>
          )}
        </div>
        {data.hasPermuta && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <Field label="Marca Usato" third>
              <input type="text" value={data.permutaMarca || ''} onChange={e => handleChange('permutaMarca', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Modello Usato" third>
              <input type="text" value={data.permutaModello || ''} onChange={e => handleChange('permutaModello', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Anno" third>
              <input type="text" value={data.permutaAnno || ''} onChange={e => handleChange('permutaAnno', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Targa Usato" third>
              <input type="text" value={data.permutaTarga || ''} onChange={e => handleChange('permutaTarga', e.target.value)} style={{ ...inputStyle, textTransform: 'uppercase' }} />
            </Field>
            <Field label="Km Usato" third>
              <input type="number" value={data.permutaKm || ''} onChange={e => handleChange('permutaKm', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Valore Permuta (€)" third>
              <input type="number" value={data.permutaValore || ''} onChange={e => handleChange('permutaValore', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Colore" third>
              <input type="text" value={data.permutaColore || ''} onChange={e => handleChange('permutaColore', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Telaio / VIN" third>
              <input type="text" value={data.permutaVin || ''} onChange={e => handleChange('permutaVin', e.target.value)} style={{ ...inputStyle, textTransform: 'uppercase' }} />
            </Field>
            <Field label="Agenzia" third>
              <input type="text" value={data.permutaAgenzia || ''} onChange={e => handleChange('permutaAgenzia', e.target.value)} style={inputStyle} />
            </Field>
            <PermuteAggiuntiveEditor lista={data.permuteAggiuntive} onChange={v => handleChange('permuteAggiuntive', v)} inputStyle={inputStyle} />
            {/* Checkbox documenti permuta */}
            <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '4px' }}>
              {[
                ['permutaCartaCircolazione', 'Carta di Circolazione'],
                ['permutaCertProprietà',     'Certificato di Proprietà'],
                ['permutaUltimoBollo',       'Copia Ultimo Bollo'],
                ['permutaSecondaChiave',     'Seconda Chiave'],
              ].map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', background: data[key] ? 'rgba(245,158,11,0.1)' : '#0d1117', padding: '6px 12px', borderRadius: '8px', border: `1px solid ${data[key] ? '#f59e0b' : '#1e2230'}` }}>
                  <input type="checkbox" checked={data[key] || false} onChange={e => handleChange(key, e.target.checked)} style={{ accentColor: '#f59e0b' }} />
                  <span style={{ fontSize: '10px', fontWeight: '700', color: data[key] ? '#f59e0b' : '#64748b' }}>{label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* FINANZIAMENTO */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '10px', fontWeight: '900', color: '#8b5cf6', letterSpacing: '1px' }}>FINANZIAMENTO</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input type="checkbox" checked={data.hasFinanziamento || false} onChange={e => handleChange('hasFinanziamento', e.target.checked)} style={{ accentColor: '#8b5cf6' }} />
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Presente</span>
          </label>
          {data.hasFinanziamento && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginLeft: 'auto', background: data.finanziamentoShowPDF ? 'rgba(139,92,246,0.15)' : 'rgba(100,116,139,0.1)', padding: '4px 10px', borderRadius: '8px', border: `1px solid ${data.finanziamentoShowPDF ? '#8b5cf6' : '#2d334a'}` }}>
              <input type="checkbox" checked={data.finanziamentoShowPDF !== false} onChange={e => handleChange('finanziamentoShowPDF', e.target.checked)} style={{ accentColor: '#8b5cf6' }} />
              <span style={{ fontSize: '10px', fontWeight: '700', color: data.finanziamentoShowPDF !== false ? '#8b5cf6' : '#64748b' }}>
                {data.finanziamentoShowPDF !== false ? '👁 Visibile in stampa' : '🚫 Nascosto in stampa'}
              </span>
            </label>
          )}
        </div>
        {data.hasFinanziamento && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <Field label="Tipo" third>
              <select value={data.tipoFinanziamento || 'Finanziamento'} onChange={e => handleChange('tipoFinanziamento', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="Finanziamento">Finanziamento</option>
                <option value="Leasing">Leasing</option>
              </select>
            </Field>
            <Field label="Importo Finanziato (€)" half>
              <input type="number" value={data.importoFinanziato || ''} onChange={e => handleChange('importoFinanziato', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Società Finanziaria" half>
              {(() => {
                const raw = data.finanziariaSocieta || '';
                const rawUpper = raw.toUpperCase().trim();
                const riconosciuta = FINANZIARIE_OPTIONS.includes(rawUpper) ? rawUpper : normalizzaFinanziaria(raw);
                const selezione = riconosciuta || (raw ? '__altro__' : '');
                return (
                  <>
                    <select value={selezione} onChange={e => handleChange('finanziariaSocieta', e.target.value === '__altro__' ? '' : e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                      <option value="">Seleziona finanziaria</option>
                      {FINANZIARIE_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                      <option value="__altro__">Altro (testo libero)</option>
                    </select>
                    {selezione === '__altro__' && (
                      <input type="text" value={riconosciuta ? '' : raw} onChange={e => handleChange('finanziariaSocieta', e.target.value)}
                        placeholder="Nome finanziaria" style={{ ...inputStyle, marginTop: '8px' }} />
                    )}
                  </>
                );
              })()}
            </Field>
            <Field label="Tasso (TAN %)">
              <input type="text" inputMode="decimal" value={data.tassoFinanziamento || ''}
                onChange={e => handleChange('tassoFinanziamento', e.target.value.replace(/[^0-9,]/g, ''))}
                style={inputStyle} placeholder="Es. 7,95" />
            </Field>
            {/* Codici tabella storici (883, 8ET, 89T, X8X, XCN → tasso) restano
                riconosciuti automaticamente da normalizeTasso() se digitati
                qui manualmente, ma il campo non forza più una scelta da
                elenco fisso — i tassi cambiano troppo spesso per un menu
                statico, meglio inserirli a mano ogni volta. */}
            <Field label="Anticipo (€)" third>
              <input type="number" value={data.finanziamentoAnticipo || ''} onChange={e => handleChange('finanziamentoAnticipo', e.target.value)} style={inputStyle} placeholder="0" />
            </Field>
            <Field label="N. Rate" third>
              <input type="number" value={data.finanziamentoNRate || ''} onChange={e => handleChange('finanziamentoNRate', e.target.value)} style={inputStyle} placeholder="Es. 48" />
            </Field>
            <Field label="Importo Rata (€/mese)" third>
              <input type="number" value={data.finanziamentoRata || ''} onChange={e => handleChange('finanziamentoRata', e.target.value)} style={inputStyle} placeholder="Es. 250" />
            </Field>
            <Field label="Maxirata (€)" half>
              <input type="number" value={data.finanziamentoMaxirata || ''} onChange={e => handleChange('finanziamentoMaxirata', e.target.value)} style={inputStyle} placeholder="Valore Futuro Garantito" />
            </Field>
            <Field label="Durata (mesi)" half>
              <input type="number" value={data.finanziamentoDurata || ''} onChange={e => handleChange('finanziamentoDurata', e.target.value)} style={inputStyle} placeholder="Es. 60" />
            </Field>
            <Field label="Tabella Finanziamento" >
              <input type="text" value={data.tabellaFinanziamento || ''} onChange={e => handleChange('tabellaFinanziamento', e.target.value)} style={inputStyle} placeholder="Es. TAN 5.99% TAEG 6.45%" />
            </Field>
            {/* Campo uso interno — non va in stampa */}
            <div style={{ width: '100%', background: 'rgba(99,102,241,0.06)', border: '1px dashed rgba(99,102,241,0.3)', borderRadius: '10px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '10px', fontWeight: '900', color: '#6366f1', letterSpacing: '0.5px' }}>🔒 USO INTERNO</span>
              <Field label="Provvigione Finanziaria (€)" half>
                <input type="number" value={data.provvigioneFinanziaria || ''} onChange={e => handleChange('provvigioneFinanziaria', e.target.value)} style={{ ...inputStyle, border: '1px solid rgba(99,102,241,0.4)' }} placeholder="Es. 500" />
              </Field>
              <span style={{ fontSize: '10px', color: '#64748b' }}>Alimenta automaticamente il Conto Economico in Finance</span>
            </div>
            <Field label="Spese di Istruttoria (€)" half>
              <input type="number" value={data.speseIstruttoria || ''} onChange={e => handleChange('speseIstruttoria', e.target.value)} style={inputStyle} placeholder="Es. 350" />
            </Field>
          </div>
        )}
      </div>

      {/* ESTINZIONE ANTICIPATA FINANZIAMENTO */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '10px', fontWeight: '900', color: '#8b5cf6', letterSpacing: '1px' }}>ESTINZIONE ANTICIPATA FINANZIAMENTO</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input type="checkbox" checked={data.estinzioneAnticipata || false} onChange={e => handleChange('estinzioneAnticipata', e.target.checked)} style={{ accentColor: '#8b5cf6' }} />
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Presente</span>
          </label>
        </div>
        {data.estinzioneAnticipata && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <Field label="Costo Estinzione Anticipata (€)" half>
              <input type="number" value={data.costoEstinzioneAnticipata || ''} onChange={e => handleChange('costoEstinzioneAnticipata', e.target.value)} style={inputStyle} placeholder="0" />
            </Field>
          </div>
        )}
      </div>

      {/* SPESE GESTIONE LEASING */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '10px', fontWeight: '900', color: '#8b5cf6', letterSpacing: '1px' }}>SPESE GESTIONE LEASING</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input type="checkbox" checked={data.speseGestioneLeasing || false} onChange={e => handleChange('speseGestioneLeasing', e.target.checked)} style={{ accentColor: '#8b5cf6' }} />
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Presente</span>
          </label>
        </div>
        {data.speseGestioneLeasing && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <Field label="Costo Spese Gestione Leasing (€)" half>
              <input type="number" value={data.costoSpeseGestioneLeasing || ''} onChange={e => handleChange('costoSpeseGestioneLeasing', e.target.value)} style={inputStyle} placeholder="0" />
            </Field>
          </div>
        )}
      </div>

      {/* CAPARRA */}
      <Field label="Caparra Confirmatoria (€)" half>
        <input type="number" value={data.caparra || ''} onChange={e => handleChange('caparra', e.target.value)} style={inputStyle} placeholder="0" />
      </Field>
      <Field label="Operatore / Venditore" half>
        <input type="text" value={data.operatore || ''} onChange={e => handleChange('operatore', e.target.value)} style={inputStyle} />
      </Field>

      {/* SCHEDA INCASSI */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: '#10b981', letterSpacing: '1px', marginBottom: '12px' }}>SCHEDA INCASSI</div>
        {(data.incassi || [{ data: '', modalita: '', importo: '' }]).map((inc, i) => (
          <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
            <input type="date" value={inc.data || ''} onChange={e => { const arr = [...(data.incassi || [{}])]; arr[i] = { ...arr[i], data: e.target.value }; handleChange('incassi', arr); }} style={{ ...inputStyle, flex: 1 }} />
            <select value={inc.modalita || ''} onChange={e => { const arr = [...(data.incassi || [{}])]; arr[i] = { ...arr[i], modalita: e.target.value }; handleChange('incassi', arr); }} style={{ ...inputStyle, flex: 2 }}>
              <option value="">Modalità...</option>
              <option value="Bonifico">Bonifico</option>
              <option value="POS">POS</option>
              <option value="Contanti">Contanti</option>
            </select>
            <input type="number" placeholder="€" value={inc.importo || ''} onChange={e => { const arr = [...(data.incassi || [{}])]; arr[i] = { ...arr[i], importo: e.target.value }; handleChange('incassi', arr); }} style={{ ...inputStyle, flex: 1 }} />
            {i > 0 && <button onClick={() => { const arr = (data.incassi || []).filter((_, j) => j !== i); handleChange('incassi', arr); }} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', flexShrink: 0 }}>×</button>}
          </div>
        ))}
        <button onClick={() => handleChange('incassi', [...(data.incassi || [{}]), { data: '', modalita: '', importo: '' }])} style={{ background: '#0a0c10', border: '1px solid #2d334a', borderRadius: '8px', color: '#3b82f6', padding: '6px 14px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', marginTop: '4px' }}>+ Aggiungi riga</button>
      </div>
    </div>
  );
}

// ─── CHECKITEM — fuori dal componente per evitare remount ad ogni keystroke ───
// ─── LoJack: selezione pacchetto con le diciture del modulo ufficiale ───────
// Si possono flaggare più pacchetti (e più modelli SmartDrive). Salva
// lojackPacchetto / lojackModello, che il contratto eredita già compilati.
const LOJACK_PACCHETTI = ['TRACKER CLASSIC', 'SMARTDRIVE CLASSIC'];
const LOJACK_MODELLI = ['LM 2630', 'LM 3030'];
// Selezione multipla: salvata come stringa "A + B" negli stessi campi di prima
// (lojackPacchetto / lojackModello) → compatibile con i dati già salvati e con
// le righe di stampa che mostrano il testo così com'è.
const lojackSplit = v => String(v || '').split('+').map(x => x.trim()).filter(Boolean);
const lojackJoin = (arr, ordine) => ordine.filter(x => arr.includes(x)).join(' + ');
const lojackToggle = (v, voce, ordine) => {
  const a = lojackSplit(v);
  return lojackJoin(a.includes(voce) ? a.filter(x => x !== voce) : [...a, voce], ordine);
};

// ─── Durata PER PACCHETTO ───────────────────────────────────────────────────
// Salvata come stringa "TRACKER CLASSIC=36|SMARTDRIVE CLASSIC=ALTRO:18 mesi"
// nel campo lojackDurate. I dati vecchi (durata unica lojackDurata /
// lojackDurataAltro) restano validi: valgono per tutti i pacchetti.
const LOJACK_DURATE = ['12', '24', '36', '48', '60', 'ALTRO'];
const lojackDurateParse = v => {
  const o = {};
  String(v || '').split('|').forEach(p => {
    const i = p.indexOf('=');
    if (i < 0) return;
    const [d, ...alt] = p.slice(i + 1).split(':');
    o[p.slice(0, i).trim()] = { d: d.trim(), altro: alt.join(':').trim() };
  });
  return o;
};
const lojackDurateSet = (v, pk, d, altro = '') => {
  const o = lojackDurateParse(v);
  if (!d) delete o[pk];
  else o[pk] = { d, altro: String(altro).replace(/[|=]/g, ' ') };
  return Object.entries(o).map(([k, x]) => `${k}=${x.d}${x.d === 'ALTRO' ? ':' + (x.altro || '') : ''}`).join('|');
};
const lojackDurataDi = (src, pk) => {
  const x = lojackDurateParse(src.lojackDurate)[pk];
  if (x && x.d) return x;
  if (src.lojackDurata) return { d: String(src.lojackDurata), altro: src.lojackDurataAltro || '' };
  return { d: '', altro: '' };
};
const lojackDurataTesto = x => (!x.d ? '' : x.d === 'ALTRO' ? (x.altro ? x.altro.toUpperCase() : 'ALTRO') : `${x.d} MESI`);
// "TRACKER CLASSIC (36 MESI) + SMARTDRIVE CLASSIC LM 3030 (24 MESI)"
const lojackDescrizione = src => {
  const mod = lojackSplit(src.lojackModello);
  return lojackSplit(src.lojackPacchetto).map(p => {
    const t = lojackDurataTesto(lojackDurataDi(src, p));
    return p + (p === 'SMARTDRIVE CLASSIC' && mod.length ? ' ' + mod.join(' + ') : '') + (t ? ` (${t})` : '');
  }).join(' + ');
};
const lojackDurateComplete = src => lojackSplit(src.lojackPacchetto)
  .every(p => { const x = lojackDurataDi(src, p); return x.d && (x.d !== 'ALTRO' || x.altro); });

// Editor pacchetti: per ogni pacchetto flaggato → modelli (SmartDrive) + durata
function LojackPacchettiEditor({ get, set }) {
  const col = '#ef4444';
  const pacchetti = lojackSplit(get('lojackPacchetto'));
  const modelli = lojackSplit(get('lojackModello'));
  const src = { lojackDurate: get('lojackDurate'), lojackDurata: get('lojackDurata'), lojackDurataAltro: get('lojackDurataAltro') };
  const pill = (on, err) => ({
    padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '800', letterSpacing: '0.5px', cursor: 'pointer',
    background: on ? `${col}25` : '#0a0c10', color: on ? '#fff' : '#9ca3af', border: `1px solid ${on ? col : err ? `${col}80` : '#1e2230'}`,
  });
  const scriviDurata = (pk, d, altro) => {
    // Alla prima durata per-pacchetto si copiano quelle "vecchie" degli altri pacchetti,
    // così un contratto salvato con la durata unica non perde nulla
    let v = get('lojackDurate') || '';
    if (!v && get('lojackDurata')) pacchetti.forEach(p => { if (p !== pk) v = lojackDurateSet(v, p, String(get('lojackDurata')), get('lojackDurataAltro') || ''); });
    v = lojackDurateSet(v, pk, d, altro);
    // Gli altri pacchetti ancora senza durata prendono la stessa (modificabile):
    // prima, con due pacchetti flaggati, bisognava ricordarsi di impostarla su
    // entrambi, altrimenti il contratto restava bloccato
    pacchetti.forEach(p => { if (p !== pk && !lojackDurataDi({ ...src, lojackDurate: v }, p).d) v = lojackDurateSet(v, p, d, altro); });
    set('lojackDurate', v);
  };
  const toggle = pk => {
    const nuovo = lojackToggle(get('lojackPacchetto'), pk, LOJACK_PACCHETTI);
    set('lojackPacchetto', nuovo);
    if (!lojackSplit(nuovo).includes(pk)) set('lojackDurate', lojackDurateSet(get('lojackDurate'), pk, ''));
    else {
      // Pacchetto aggiunto: se un altro ha già una durata, parte con la stessa
      const altra = pacchetti.map(p => lojackDurataDi(src, p)).find(x => x.d);
      if (altra && !lojackDurataDi(src, pk).d) set('lojackDurate', lojackDurateSet(get('lojackDurate'), pk, altra.d, altra.altro));
    }
    if (!lojackSplit(nuovo).includes('SMARTDRIVE CLASSIC')) set('lojackModello', '');
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {LOJACK_PACCHETTI.map(pk => {
        const on = pacchetti.includes(pk);
        const dur = lojackDurataDi(src, pk);
        return (
          <div key={pk} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', padding: on ? '8px' : '0', borderRadius: '8px', background: on ? '#0a0c10' : 'transparent', border: on ? '1px solid #1e2230' : 'none' }}>
            <button type="button" onClick={() => toggle(pk)} style={{ ...pill(on, !pacchetti.length), minWidth: '150px', textAlign: 'left' }}>{on ? '✓ ' : ''}{pk}</button>
            {on && pk === 'SMARTDRIVE CLASSIC' && (
              <>
                {LOJACK_MODELLI.map(m => (
                  <button key={m} type="button" onClick={() => set('lojackModello', lojackToggle(get('lojackModello'), m, LOJACK_MODELLI))} style={pill(modelli.includes(m), !modelli.length)}>{modelli.includes(m) ? '✓ ' : ''}{m}</button>
                ))}
                <span style={{ width: '1px', height: '16px', background: '#1e2230', margin: '0 2px' }} />
              </>
            )}
            {on && (
              <>
                <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: '700' }}>Durata</span>
                {LOJACK_DURATE.map(d => (
                  <button key={d} type="button" onClick={() => scriviDurata(pk, d, d === 'ALTRO' ? dur.altro : '')} style={pill(dur.d === d, !dur.d)}>
                    {d === 'ALTRO' ? 'ALTRO' : `${d} MESI`}
                  </button>
                ))}
                {dur.d === 'ALTRO' && (
                  <input value={dur.altro} onChange={e => scriviDurata(pk, 'ALTRO', e.target.value)} placeholder="es. 18 mesi"
                    style={{ width: '100px', padding: '4px 8px', background: '#0a0c10', border: `1px solid ${dur.altro ? '#1e2230' : col}`, borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }} />
                )}
              </>
            )}
          </div>
        );
      })}
      {!pacchetti.length && <span style={{ fontSize: '10px', color: col }}>Seleziona uno o più pacchetti</span>}
      {pacchetti.includes('SMARTDRIVE CLASSIC') && !modelli.length && <span style={{ fontSize: '10px', color: col }}>Seleziona il modello SmartDrive</span>}
      {pacchetti.length > 0 && !lojackDurateComplete({ ...src, lojackPacchetto: get('lojackPacchetto') }) && <span style={{ fontSize: '10px', color: col }}>Indica la durata di ogni pacchetto</span>}
    </div>
  );
}
function LojackItem({ get, set, compact }) {
  const attivo = !!get('lojack');
  const col = '#ef4444';
  const toggle = (on) => {
    set('lojack', on);
    if (!on) { set('costoLojack', 0); set('lojackPacchetto', ''); set('lojackModello', ''); set('lojackDurate', ''); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: compact ? '0' : '8px', background: compact ? 'transparent' : (attivo ? `${col}15` : '#0a0c10'), borderRadius: '8px', border: compact ? 'none' : `1px solid ${attivo ? col : '#1e2230'}`, transition: 'all 0.15s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1, minWidth: 0 }}>
          <input type="checkbox" checked={attivo} onChange={e => toggle(e.target.checked)} style={{ accentColor: col, width: 14, height: 14, flexShrink: 0 }} />
          <span style={{ fontSize: '11px', fontWeight: '700', color: attivo ? '#fff' : '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Localizzatore radiofrequenze LoJack con centrale operativa H24</span>
        </label>
        {attivo && (
          <input type="number" value={get('costoLojack') || ''} onChange={e => set('costoLojack', e.target.value)} placeholder="€"
            style={{ width: '72px', padding: '4px 8px', background: '#0a0c10', border: `1px solid ${col}`, borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', flexShrink: 0 }} />
        )}
      </div>
      {attivo && (
        <div style={{ paddingLeft: '22px' }}>
          <LojackPacchettiEditor get={get} set={(k, v) => { if (!get('lojack')) set('lojack', true); set(k, v); }} />
        </div>
      )}
    </div>
  );
}

// ─── PACK MANUTENZIONE (3 o 4 tagliandi) ────────────────────────────────────
// Voce separata da "Tagliando". Provvigione consulente: 3 → 20 €, 4 → 25 €.
const PACK_MANUTENZIONE_TAGLIANDI = ['3', '4'];
function PackManutenzioneItem({ get, set }) {
  const col = '#8b5cf6';
  const attivo = !!get('packManutenzione');
  const n = String(get('packManutenzioneTagliandi') || '');
  const toggle = on => {
    set('packManutenzione', on);
    if (on && !n) set('packManutenzioneTagliandi', '3');
    if (!on) { set('costoPackManutenzione', 0); set('packManutenzioneTagliandi', ''); }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: attivo ? `${col}15` : '#0a0c10', borderRadius: '8px', border: `1px solid ${attivo ? col : '#1e2230'}`, flexWrap: 'wrap' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1, minWidth: '140px' }}>
        <input type="checkbox" checked={attivo} onChange={e => toggle(e.target.checked)} style={{ accentColor: col, width: 14, height: 14 }} />
        <span style={{ fontSize: '11px', fontWeight: '700', color: attivo ? '#fff' : '#9ca3af' }}>Pack manutenzione</span>
      </label>
      {attivo && (
        <>
          <select value={n} onChange={e => set('packManutenzioneTagliandi', e.target.value)}
            style={{ padding: '4px 8px', background: '#0a0c10', border: `1px solid ${col}`, borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', cursor: 'pointer' }}>
            {PACK_MANUTENZIONE_TAGLIANDI.map(x => <option key={x} value={x}>{x} tagliandi</option>)}
          </select>
          <input type="number" value={get('costoPackManutenzione') || ''} onChange={e => set('costoPackManutenzione', e.target.value)} placeholder="€"
            style={{ width: '72px', padding: '4px 8px', background: '#0a0c10', border: `1px solid ${col}`, borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none' }} />
        </>
      )}
    </div>
  );
}
const etichettaPackManutenzione = sv => `Pack manutenzione${sv?.packManutenzioneTagliandi ? ` (${sv.packManutenzioneTagliandi} tagliandi)` : ''}`;

// ─── PERMUTE AGGIUNTIVE (oltre alla prima) ─────────────────────────────────
// La prima permuta resta nei campi storici (permutaMarca, permutaValore…);
// le altre in prezzo.permuteAggiuntive. Il totale da scalare è la somma.
const valorePermuteTot = pr => (parseFloat(pr?.permutaValore) || 0)
  + (Array.isArray(pr?.permuteAggiuntive) ? pr.permuteAggiuntive : []).reduce((t, x) => t + (parseFloat(x?.valore) || 0), 0);
function PermuteAggiuntiveEditor({ lista, onChange, inputStyle }) {
  const arr = Array.isArray(lista) ? lista : [];
  const up = (i, k, v) => onChange(arr.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const campi = [['marca', 'Marca'], ['modello', 'Modello'], ['targa', 'Targa'], ['vin', 'Telaio / VIN'], ['anno', 'Anno'], ['km', 'Km'], ['valore', 'Valore (€)']];
  return (
    <div style={{ width: '100%', marginTop: '6px' }}>
      {arr.map((p, i) => (
        <div key={i} style={{ border: '1px dashed #f59e0b60', borderRadius: '10px', padding: '10px', marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ fontSize: '10px', fontWeight: '900', color: '#f59e0b', letterSpacing: '1px' }}>PERMUTA {i + 2}</div>
            <button type="button" onClick={() => { if (window.confirm(`Rimuovere la permuta ${i + 2}?`)) onChange(arr.filter((_, j) => j !== i)); }}
              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>✕ Rimuovi</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
            {campi.map(([k, l]) => (
              <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: '700' }}>{l}</span>
                <input type={k === 'km' || k === 'valore' ? 'number' : 'text'} value={p[k] || ''}
                  onChange={e => up(i, k, k === 'targa' || k === 'vin' ? e.target.value.toUpperCase() : e.target.value)}
                  style={inputStyle} />
              </label>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...arr, { marca: '', modello: '', targa: '', vin: '', anno: '', km: '', valore: '' }])}
        style={{ padding: '7px 12px', background: 'rgba(245,158,11,0.08)', border: '1px dashed #f59e0b', borderRadius: '8px', color: '#f59e0b', cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>
        + Aggiungi altra permuta
      </button>
    </div>
  );
}

function CheckItem({ field, costField, label, color = '#3b82f6', data, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: data[field] ? `${color}15` : '#0a0c10', borderRadius: '8px', border: `1px solid ${data[field] ? color : '#1e2230'}`, transition: 'all 0.15s' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1, minWidth: 0 }}>
        <input type="checkbox" checked={data[field] || false} onChange={e => {
          onChange(field, e.target.checked);
          if (!e.target.checked && costField) onChange(costField, 0); // azzera il costo quando si deseleziona
        }} style={{ accentColor: color, width: 14, height: 14, flexShrink: 0 }} />
        <span style={{ fontSize: '11px', fontWeight: '700', color: data[field] ? '#fff' : '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      </label>
      {data[field] && (
        <input
          type="number"
          value={data[costField] || ''}
          onChange={e => onChange(costField, e.target.value)}
          onClick={e => e.stopPropagation()}
          placeholder="€"
          style={{ width: '72px', padding: '4px 8px', background: '#0a0c10', border: `1px solid ${color}`, borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', flexShrink: 0 }}
        />
      )}
    </div>
  );
}

// ─── STEP 4: SERVIZI ──────────────────────────────────────────────────────────
// ─── Beyonder — ricerca allestimenti veicoli commerciali/industriali ──────
// Chiama sempre il proxy locale (notify-server.js), mai Beyonder
// direttamente — la chiave API resta solo lato server.
// 1° tentativo: proxy PHP su radargas.it (sempre attivo, raggiungibile da
// ovunque). 2° tentativo (fallback): notify-server locale, solo se il PC
// dell'ufficio è acceso e si è sulla stessa rete — stesso identico schema
// già usato per LeadSpark.
const BEYONDER_PHP_PROXY = 'https://radargas.it/beyonder-proxy.php';
const BEYONDER_LOCAL_PROXY = 'http://localhost:3002';

function BeyonderModal({ onClose, onConferma }) {
  const [caricando, setCaricando] = useState(true);
  const [errore, setErrore] = useState('');
  const [catalogo, setCatalogo] = useState(null);
  const [catalogoSel, setCatalogoSel] = useState('');
  const [veicoloSel, setVeicoloSel] = useState('');
  const [versioneSel, setVersioneSel] = useState('');
  const [prodottiSel, setProdottiSel] = useState({});

  useEffect(() => {
    const caricaCatalogo = async () => {
      // 1. Proxy PHP pubblico (sempre disponibile)
      try {
        const r = await fetch(`${BEYONDER_PHP_PROXY}?action=catalog&locale=it`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const dati = await r.json();
        setCatalogo(dati);
        setCaricando(false);
        return;
      } catch (e) {
        console.warn('Beyonder: proxy PHP fallito, provo notify-server locale:', e.message);
      }
      // 2. Fallback notify-server locale
      try {
        const r = await fetch(`${BEYONDER_LOCAL_PROXY}/beyonder/catalog?locale=it`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const dati = await r.json();
        setCatalogo(dati);
      } catch (e) {
        setErrore('Impossibile contattare il servizio Beyonder, né tramite il proxy pubblico né in locale. Riprova più tardi.');
      }
      setCaricando(false);
    };
    caricaCatalogo();
  }, []);

  const catalogoAttivo = catalogo?.catalogs?.find(c => c.code === catalogoSel);
  const veicoloAttivo = catalogoAttivo?.vehicles?.find(v => v.code === veicoloSel);
  const versioneAttiva = veicoloAttivo?.versions?.find(v => v.code === versioneSel);

  const prodottiConPrezzo = (catalogoAttivo?.products || []).map(p => ({
    ...p,
    prezzoVersione: versioneSel ? p.prices?.find(pr => pr.versionCode === versioneSel) : null,
  })).filter(p => p.prezzoVersione);

  const totale = Object.entries(prodottiSel).filter(([, v]) => v).reduce((tot, [codice]) => {
    const p = prodottiConPrezzo.find(x => x.code === codice);
    return tot + (p?.prezzoVersione?.amount || 0);
  }, 0);

  const nomiSelezionati = Object.entries(prodottiSel).filter(([, v]) => v).map(([codice]) => prodottiConPrezzo.find(x => x.code === codice)?.name).filter(Boolean);

  const handleConferma = () => {
    if (nomiSelezionati.length === 0) { alert('Seleziona almeno un allestimento/prodotto.'); return; }
    const descrizione = `Beyonder: ${veicoloAttivo?.name || ''} ${versioneAttiva?.label || ''} — ${nomiSelezionati.join(', ')}`.trim();
    onConferma(descrizione, Math.round(totale));
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#12141c', border: '1px solid #1e2230', borderRadius: '18px', maxWidth: '560px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #1e2230', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '15px', fontWeight: '900', color: '#fff' }}>🚛 Beyonder — Cerca Allestimento</div>
          <button onClick={onClose} style={{ background: '#0a0c10', border: 'none', color: '#fff', borderRadius: '10px', padding: '6px 11px', cursor: 'pointer', fontWeight: '900', fontSize: '16px' }}>×</button>
        </div>

        <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>
          {caricando && <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontSize: '13px' }}>⏳ Caricamento catalogo...</div>}
          {errore && <div style={{ fontSize: '12px', color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '12px 14px', lineHeight: 1.6 }}>⚠️ {errore}</div>}

          {catalogo && (
            <>
              <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b', display: 'block', marginBottom: '6px' }}>CATALOGO</label>
              <select value={catalogoSel} onChange={e => { setCatalogoSel(e.target.value); setVeicoloSel(''); setVersioneSel(''); setProdottiSel({}); }} style={{ ...inputStyle, cursor: 'pointer', marginBottom: '14px' }}>
                <option value="">Seleziona catalogo...</option>
                {(catalogo.catalogs || []).map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>

              {catalogoAttivo && (
                <>
                  <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b', display: 'block', marginBottom: '6px' }}>VEICOLO</label>
                  <select value={veicoloSel} onChange={e => { setVeicoloSel(e.target.value); setVersioneSel(''); setProdottiSel({}); }} style={{ ...inputStyle, cursor: 'pointer', marginBottom: '14px' }}>
                    <option value="">Seleziona veicolo...</option>
                    {(catalogoAttivo.vehicles || []).map(v => <option key={v.code} value={v.code}>{v.name}</option>)}
                  </select>
                </>
              )}

              {veicoloAttivo && (
                <>
                  <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b', display: 'block', marginBottom: '6px' }}>VERSIONE</label>
                  <select value={versioneSel} onChange={e => { setVersioneSel(e.target.value); setProdottiSel({}); }} style={{ ...inputStyle, cursor: 'pointer', marginBottom: '14px' }}>
                    <option value="">Seleziona versione...</option>
                    {(veicoloAttivo.versions || []).map(v => <option key={v.code} value={v.code}>{v.label}</option>)}
                  </select>
                </>
              )}

              {versioneAttiva && (
                <>
                  <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b', display: 'block', marginBottom: '8px' }}>ALLESTIMENTI DISPONIBILI</label>
                  {prodottiConPrezzo.length === 0 ? (
                    <div style={{ fontSize: '11px', color: '#64748b', textAlign: 'center', padding: '14px' }}>Nessun prodotto disponibile per questa versione.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {prodottiConPrezzo.map(p => (
                        <label key={p.code} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', background: prodottiSel[p.code] ? 'rgba(245,158,11,0.1)' : '#0a0c10', border: `1px solid ${prodottiSel[p.code] ? '#f59e0b' : '#1e2230'}`, borderRadius: '9px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!prodottiSel[p.code]} onChange={e => setProdottiSel(prev => ({ ...prev, [p.code]: e.target.checked }))} style={{ width: '15px', height: '15px', accentColor: '#f59e0b', flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#fff' }}>{p.name}</div>
                            {p.description && <div style={{ fontSize: '10px', color: '#64748b' }}>{p.description}</div>}
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: '800', color: '#f59e0b', whiteSpace: 'nowrap' }}>
                            {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(p.prezzoVersione.amount)}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {catalogo && (
          <div style={{ padding: '14px 22px', borderTop: '1px solid #1e2230', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>
              Totale: <b style={{ color: '#f59e0b', fontSize: '15px' }}>{new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(totale)}</b>
              <span style={{ fontSize: '9px', color: '#64748b' }}> (IVA esclusa, come da listino Beyonder)</span>
            </div>
            <button onClick={handleConferma} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '10px', color: '#0f172a', cursor: 'pointer', fontWeight: '900', fontSize: '12px', whiteSpace: 'nowrap' }}>
              ✅ Aggiungi al Preventivo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Step4Servizi({ data, onChange, veicolo }) {
  const [beyonderOpen, setBeyonderOpen] = useState(false);

  const isNuovo = (veicolo?.tipo || '').toLowerCase() === 'nuovo';
  const accessoriList = data.accessoriCasaMadreList || [];

  const addAccessorio = () => {
    onChange('accessoriCasaMadreList', [...accessoriList, { id: Date.now(), nome: '', prezzo: '' }]);
  };
  const updateAccessorio = (id, field, value) => {
    onChange('accessoriCasaMadreList', accessoriList.map(a => a.id === id ? { ...a, [field]: value } : a));
  };
  const removeAccessorio = (id) => {
    onChange('accessoriCasaMadreList', accessoriList.filter(a => a.id !== id));
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>

      {/* OPTIONAL CASA MADRE — solo veicoli nuovi */}
      {isNuovo && (
        <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', fontWeight: '900', color: '#3b82f6', letterSpacing: '1px' }}>OPTIONAL CASA MADRE</div>
            <button onClick={addAccessorio} style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid #3b82f6', borderRadius: '8px', padding: '4px 10px', color: '#3b82f6', cursor: 'pointer', fontSize: '10px', fontWeight: '700' }}>
              + Aggiungi optional
            </button>
          </div>
          {accessoriList.length === 0 && (
            <div style={{ fontSize: '10px', color: '#64748b', padding: '8px 0' }}>Nessun optional aggiunto. Clicca "+ Aggiungi optional" per inserirne uno.</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {accessoriList.map(acc => (
              <div key={acc.id} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={acc.nome}
                  onChange={e => updateAccessorio(acc.id, 'nome', e.target.value)}
                  placeholder="Nome optional (es. Cerchi in lega 18'')"
                  style={{ flex: 1, padding: '10px 12px', background: '#0a0c10', border: '1px solid #1e2230', borderRadius: '8px', color: '#fff', fontSize: '11px', outline: 'none' }}
                />
                <input
                  type="number"
                  value={acc.prezzo}
                  onChange={e => updateAccessorio(acc.id, 'prezzo', e.target.value)}
                  placeholder="€"
                  style={{ width: '100px', padding: '10px 12px', background: '#0a0c10', border: '1px solid #3b82f6', borderRadius: '8px', color: '#fff', fontSize: '11px', outline: 'none' }}
                />
                <button onClick={() => removeAccessorio(acc.id)} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '8px 10px', color: '#ef4444', cursor: 'pointer', fontSize: '12px' }}>✕</button>
              </div>
            ))}
          </div>
          {accessoriList.length > 0 && (
            <div style={{ marginTop: '10px', textAlign: 'right', fontSize: '11px', color: '#3b82f6', fontWeight: '900' }}>
              Totale: {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(accessoriList.reduce((s,a) => s + (parseFloat(a.prezzo)||0), 0))}
            </div>
          )}
        </div>
      )}

      {/* ANTIFURTO */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: '#ef4444', letterSpacing: '1px', marginBottom: '12px' }}>ANTIFURTO</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <LojackItem get={k => data[k]} set={onChange} />
          <CheckItem field="blockshaft" costField="costoBlockshaft" label="Blockshaft" color="#ef4444" data={data} onChange={onChange} />
          {/* Ext. Garanzia Mawdy — un solo checkbox con selezione mesi, invece
              di un'opzione fissa a 36. Il campo "garanziaEstesaMesi" è già
              quello letto per primo dal calcolo provvigioni (struttura
              invariata) — qui lo colleghiamo semplicemente a un'interfaccia
              vera invece di lasciarlo senza controllo dedicato. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: data.mawdy ? '#8b5cf615' : '#0a0c10', borderRadius: '8px', border: `1px solid ${data.mawdy ? '#8b5cf6' : '#1e2230'}`, transition: 'all 0.15s' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flexShrink: 0 }}>
              <input type="checkbox" checked={data.mawdy || false} onChange={e => {
                onChange('mawdy', e.target.checked);
                if (!e.target.checked) { onChange('costoMawdy', 0); onChange('garanziaEstesaMesi', ''); }
                else if (!data.garanziaEstesaMesi) onChange('garanziaEstesaMesi', 36);
              }} style={{ accentColor: '#8b5cf6', width: 14, height: 14, flexShrink: 0 }} />
              <span style={{ fontSize: '11px', fontWeight: '700', color: data.mawdy ? '#fff' : '#9ca3af', whiteSpace: 'nowrap' }}>Ext. Garanzia Mawdy</span>
            </label>
            {data.mawdy && (
              <>
                <select value={data.garanziaEstesaMesi || 36} onChange={e => onChange('garanziaEstesaMesi', parseInt(e.target.value))}
                  style={{ padding: '4px 6px', background: '#0a0c10', border: '1px solid #8b5cf6', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', cursor: 'pointer', flexShrink: 0 }}>
                  {[12, 24, 36, 48, 60].map(m => <option key={m} value={m}>{m} mesi</option>)}
                </select>
                <input type="number" value={data.costoMawdy || ''} onChange={e => onChange('costoMawdy', e.target.value)}
                  placeholder="€" style={{ width: '72px', padding: '4px 8px', background: '#0a0c10', border: '1px solid #8b5cf6', borderRadius: '6px', color: '#fff', fontSize: '11px', outline: 'none', flexShrink: 0, marginLeft: 'auto' }} />
              </>
            )}
          </div>
        </div>
        <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="text" value={data.altroAntifurto || ''} onChange={e => onChange('altroAntifurto', e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Altro antifurto..." />
          {data.altroAntifurto && <input type="number" value={data.costoAltroAntifurto || ''} onChange={e => onChange('costoAltroAntifurto', e.target.value)} placeholder="€" style={{ width: '80px', padding: '10px 8px', background: '#0a0c10', border: '1px solid #ef4444', borderRadius: '10px', color: '#fff', fontSize: '11px', outline: 'none' }} />}
        </div>
      </div>

      {/* ASSICURAZIONI */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: '#3b82f6', letterSpacing: '1px', marginBottom: '12px' }}>ASSICURAZIONI</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[
            { field: 'assPP',  costField: 'costoAssPP',  durField: 'durAssPP',  inclField: 'inclAssPP',  label: 'PP — Protezione Pagamenti' },
            { field: 'assFIR', costField: 'costoAssFIR', durField: 'durAssFIR', inclField: 'inclAssFIR', label: 'FIR — Furto Incendio Rischi' },
            { field: 'assGAP', costField: 'costoAssGAP', durField: 'durAssGAP', inclField: 'inclAssGAP', label: 'GAP — Valore a Nuovo' },
          ].map(({ field, costField, durField, inclField, label }) => (
            <div key={field}>
              <CheckItem field={field} costField={costField} label={label} color="#3b82f6" data={data} onChange={onChange} />
              {data[field] && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', paddingLeft: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '10px', color: '#64748b', fontWeight: '700' }}>Durata:</span>
                    {[24, 36, 48, 60].map(m => (
                      <button key={m} onClick={() => onChange(durField, (data[durField] || 0) === m ? 0 : m)}
                        style={{
                          padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '700',
                          border: `1px solid ${(data[durField]||0) === m ? '#3b82f6' : '#1e2230'}`,
                          background: (data[durField]||0) === m ? 'rgba(59,130,246,0.15)' : '#0a0c10',
                          color: (data[durField]||0) === m ? '#3b82f6' : '#64748b',
                        }}>
                        {m} mesi
                      </button>
                    ))}
                    {(data[durField]||0) > 0 && (
                      <span style={{ fontSize: '10px', color: '#3b82f6', fontWeight: '700' }}>✓ {data[durField]} mesi</span>
                    )}
                  </div>
                  <input type="text" value={data[inclField] || ''} onChange={e => onChange(inclField, e.target.value)}
                    placeholder="Servizi inclusi (riportato in preventivo e contratto)..."
                    style={{ ...inputStyle, width: '100%', marginTop: '8px', boxSizing: 'border-box' }} />
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* GARANZIA */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: '#10b981', letterSpacing: '1px', marginBottom: '12px' }}>GARANZIA ESTESA</div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '11px', color: '#94a3b8' }}>Mesi garanzia:</div>
          {[0, 12, 24, 36, 48, 60].map(m => (
            <button key={m} onClick={() => onChange('garanziaEstesaMesi', m === (data.garanziaEstesaMesi || 0) ? 0 : m)}
              style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${(data.garanziaEstesaMesi || 0) === m && m > 0 ? '#10b981' : '#1e2230'}`, background: (data.garanziaEstesaMesi || 0) === m && m > 0 ? 'rgba(16,185,129,0.15)' : '#0a0c10', color: (data.garanziaEstesaMesi || 0) === m && m > 0 ? '#10b981' : '#64748b', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
              {m === 0 ? 'Nessuna' : `${m}m`}
            </button>
          ))}
          {(data.garanziaEstesaMesi || 0) > 0 && (
            <input type="number" value={data.costoGaranzia || ''} onChange={e => onChange('costoGaranzia', e.target.value)}
              placeholder="€ costo" style={{ width: '90px', padding: '6px 10px', background: '#0a0c10', border: '1px solid #10b981', borderRadius: '8px', color: '#fff', fontSize: '11px', outline: 'none' }} />
          )}
        </div>
      </div>

      {/* SERVICE CARE GRUPPO AUTOSCALA */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '10px', fontWeight: '900', color: '#3b82f6', letterSpacing: '1px' }}>SERVICE CARE GRUPPO AUTOSCALA</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input type="checkbox" checked={data.serviceCare || false} onChange={e => onChange('serviceCare', e.target.checked)} style={{ accentColor: '#3b82f6' }} />
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Presente</span>
          </label>
        </div>
        <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '10px' }}>Pacchetto manutenzione: olio motore, filtro olio, filtro aria, filtro abitacolo inclusi.</div>
        {data.serviceCare && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '11px', color: '#94a3b8' }}>Durata:</div>
            {[36, 48].map(m => (
              <button key={m} onClick={() => {
                onChange('serviceCareMesi', m === data.serviceCareMesi ? 0 : m);
                const ccRisolta = veicolo?.cc || estraiCilindrataTesto(veicolo?.versione) || estraiCilindrataTesto(veicolo?.optionals);
                const suggerito = calcolaPrezzoServiceCare(ccRisolta, veicolo?.carburante, m);
                if (suggerito && m !== data.serviceCareMesi) onChange('costoServiceCare', suggerito);
              }}
                style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${data.serviceCareMesi === m ? '#3b82f6' : '#1e2230'}`, background: data.serviceCareMesi === m ? 'rgba(59,130,246,0.15)' : '#0a0c10', color: data.serviceCareMesi === m ? '#3b82f6' : '#64748b', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
                {m} mesi
              </button>
            ))}
            {data.serviceCareMesi > 0 && (
              <>
                <input type="number" value={data.costoServiceCare || ''} onChange={e => onChange('costoServiceCare', e.target.value)}
                  placeholder="€ costo" style={{ width: '90px', padding: '6px 10px', background: '#0a0c10', border: '1px solid #3b82f6', borderRadius: '8px', color: '#fff', fontSize: '11px', outline: 'none' }} />
                {calcolaPrezzoServiceCare(veicolo?.cc || estraiCilindrataTesto(veicolo?.versione) || estraiCilindrataTesto(veicolo?.optionals), veicolo?.carburante, data.serviceCareMesi) === null && (
                  <span style={{ fontSize: '9px', color: '#f59e0b' }}>ℹ️ Cilindrata veicolo non disponibile — inserisci il prezzo manualmente</span>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* INSTALLAZIONI */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: '#f59e0b', letterSpacing: '1px', marginBottom: '12px' }}>INSTALLAZIONI</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <CheckItem field="gpl" costField="costoGpl" label="GPL" color="#f59e0b" data={data} onChange={onChange} />
          <CheckItem field="n1" costField="costoN1" label="N1 — Trasformazione Autocarro" color="#f59e0b" data={data} onChange={onChange} />
          <CheckItem field="kitAutocarro" costField="costoKitAutocarro" label="Kit Autocarro" color="#f59e0b" data={data} onChange={onChange} />
        </div>
        <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="text" value={data.altroInstallazione || ''} onChange={e => onChange('altroInstallazione', e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Altra installazione..." />
          {data.altroInstallazione && <input type="number" value={data.costoAltroInstallazione || ''} onChange={e => onChange('costoAltroInstallazione', e.target.value)} placeholder="€" style={{ width: '80px', padding: '10px 8px', background: '#0a0c10', border: '1px solid #f59e0b', borderRadius: '10px', color: '#fff', fontSize: '11px', outline: 'none' }} />}
        </div>
      </div>

      {/* ACCESSORI */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: '#10b981', letterSpacing: '1px', marginBottom: '12px' }}>ACCESSORI</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <CheckItem field="sensoriParcheggio" costField="costoSensoriParcheggio" label="Sensori Parcheggio Ant/Post" color="#10b981" data={data} onChange={onChange} />
          <CheckItem field="retrocamera" costField="costoRetrocamera" label="Retrocamera" color="#10b981" data={data} onChange={onChange} />
          <CheckItem field="bracciolo" costField="costoBracciolo" label="Bracciolo" color="#10b981" data={data} onChange={onChange} />
          <CheckItem field="vetriPrivacy" costField="costoVetriPrivacy" label="Vetri Privacy" color="#10b981" data={data} onChange={onChange} />
        </div>
        <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="text" value={data.altroAccessorio || ''} onChange={e => onChange('altroAccessorio', e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Altro accessorio..." />
          {data.altroAccessorio && <input type="number" value={data.costoAltroAccessorio || ''} onChange={e => onChange('costoAltroAccessorio', e.target.value)} placeholder="€" style={{ width: '80px', padding: '10px 8px', background: '#0a0c10', border: '1px solid #10b981', borderRadius: '10px', color: '#fff', fontSize: '11px', outline: 'none' }} />}
        </div>
      </div>

      {/* BEYONDER — allestimenti veicoli commerciali/industriali */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '10px', fontWeight: '900', color: '#f59e0b', letterSpacing: '1px' }}>BEYONDER — VEICOLI COMMERCIALI/INDUSTRIALI</div>
            <div style={{ fontSize: '9px', color: '#64748b', marginTop: '4px' }}>Cerca un allestimento dal configuratore Beyonder e aggiungilo come servizio extra</div>
          </div>
          <button type="button" onClick={() => setBeyonderOpen(true)}
            style={{ padding: '9px 16px', background: 'rgba(245,158,11,0.12)', border: '1px solid #f59e0b', borderRadius: '10px', color: '#f59e0b', cursor: 'pointer', fontWeight: '900', fontSize: '11px', whiteSpace: 'nowrap' }}>
            🚛 Cerca Allestimento
          </button>
        </div>
      </div>

      {beyonderOpen && (
        <BeyonderModal
          onClose={() => setBeyonderOpen(false)}
          onConferma={(descrizione, costo) => {
            onChange('altroAccessorio', descrizione);
            onChange('costoAltroAccessorio', costo);
            setBeyonderOpen(false);
          }}
        />
      )}

      {/* MECCANICA */}
      <div style={{ width: '100%', background: '#141722', borderRadius: '12px', padding: '16px', border: '1px solid #1e2230' }}>
        <div style={{ fontSize: '10px', fontWeight: '900', color: '#8b5cf6', letterSpacing: '1px', marginBottom: '12px' }}>MECCANICA / CARROZZERIA</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          <CheckItem field="tagliando" costField="costoTagliando" label="Tagliando" color="#8b5cf6" data={data} onChange={onChange} />
          <PackManutenzioneItem get={k => data[k]} set={onChange} />
          <CheckItem field="revisione" costField="costoRevisione" label="Revisione" color="#8b5cf6" data={data} onChange={onChange} />
          <CheckItem field="secondaChiave" costField="costoSecondaChiave" label="2a Chiave" color="#8b5cf6" data={data} onChange={onChange} />
        </div>
        <Field label="Lavorazioni carrozzeria">
          <textarea value={data.lavorazioniCarrozzeria || ''} onChange={e => onChange('lavorazioniCarrozzeria', e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Descrivere lavorazioni..." />
        </Field>
        {data.lavorazioniCarrozzeria && (
          <div style={{ marginTop: '8px' }}>
            <Field label="Costo lavorazioni carrozzeria (EUR)">
              <input type="number" value={data.costoLavorazioniCarrozzeria || ''} onChange={e => onChange('costoLavorazioniCarrozzeria', e.target.value)} style={inputStyle} placeholder="0" />
            </Field>
          </div>
        )}
      </div>

      {/* NOTE */}
      <div style={{ width: '100%' }}>
        <Field label="Note aggiuntive">
          <textarea value={data.note || ''} onChange={e => onChange('note', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Note libere..." />
        </Field>
      </div>
    </div>
  );
}

// ─── GENERATE PDF ──────────────────────────────────────────────────────────────
// ─── RECAPITI CONSULENTI (stampati sul preventivo) ──────────────────────────
// Per aggiungere un consulente: una riga [nome, email, cellulare].
const RECAPITI_CONSULENTI = [
  ['Claudio Filosa', 'claudio.filosa@gruppoautoscala.com', '351 524 6948'],
  ['Simone Bruno', 'simone.bruno@gruppoautoscala.com', '340 452 4066'],
  ['Davide Zaritto', 'davide.zaritto@gruppoautoscala.com', '342 628 7712'],
  ['Enrico Palumbo', 'enrico.palumbo@gruppoautoscala.com', '345 028 1967'],
  ['Francesco Montuori', 'francesco.montuori@gruppoautoscala.com', '340 743 8120'],
  ['Guido Fiore', 'guido.fiore@gruppoautoscala.com', '348 120 9865'],
  ['Luca Ambrosino', 'luca.ambrosino@gruppoautoscala.com', '348 707 6668'],
  ['Marco Castaldo', 'marco.castaldo@gruppoautoscala.com', '340 344 2496'],
  ['Mattia Zuppa', 'mattia.zuppa@gruppoautoscala.com', '346 083 8683'],
  ['Pasquale Del Villano', 'pasquale.delvillano@gruppoautoscala.com', '349 767 2742'],
  ['Roberto Castaldo', 'roberto.castaldo@gruppoautoscala.com', '331 704 9039'],
  ['Claudio Gerardi', 'claudio.gerardi@gruppoautoscala.com', '328 488 8076'],
  ['Rosario Scala', 'rosario.scala@gruppoautoscala.com', '339 506 8174'],
];
const normNomeCons = s => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z]/g, '');
// Trova i recapiti dal nome del venditore (ignora maiuscole, spazi, accenti e ordine nome/cognome)
export function recapitiConsulente(nome, rubrica = RECAPITI_CONSULENTI) {
  const n = normNomeCons(nome);
  if (!n) return null;
  const r = rubrica.find(([nm]) => {
    const parti = String(nm).toLowerCase().split(/\s+/);
    return normNomeCons(nm) === n || normNomeCons([...parti].reverse().join(' ')) === n;
  });
  return r ? { nome: r[0], email: r[1], cell: r[2] } : null;
}

export async function generatePDF(veicolo, cliente, prezzo, servizi) {
  if (!window.jspdf) {
    await new Promise((resolve, reject) => {
      const sc = document.createElement('script');
      sc.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      sc.onload = resolve; sc.onerror = reject;
      document.head.appendChild(sc);
    });
  }

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, H = 297, ML = 12, MR = 12;

  // Lookup live nello stock (stessa fonte dati della tab Stock) — veicolo.colore
  // può essere uno snapshot congelato, corretto dopo direttamente in stock.
  // La cache window._radarStockCache può non essere mai stata popolata in
  // questa sessione: qui la ricarichiamo sempre fresca da Firebase.
  try {
    const dbLive = getDatabase();
    const snapStock = await get(ref(dbLive, 'stock_data'));
    if (snapStock.exists() && snapStock.val()?.vehicles) {
      window._radarStockCache = snapStock.val();
    }
  } catch (e) { /* offline: si userà l'eventuale cache già presente */ }
  const norm = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');
  const stockLiveV = (window._radarStockCache?.vehicles || []).find(sv =>
    (veicolo.id && sv.id === veicolo.id) ||
    (veicolo.targa && norm(sv.targa) === norm(veicolo.targa)) ||
    (veicolo.vin && norm(sv.vin) === norm(veicolo.vin))
  );
  const CW = W - ML - MR; // larghezza contenuto

  // Palette
  const BLACK = [0,0,0], WHITE = [255,255,255];
  const LGRAY = [248,248,248], MGRAY = [210,210,210], DGRAY = [100,100,100];
  const BLUE  = [30,75,170],   GREEN = [20,120,75];

  const sv = v => {
    if (v === null || v === undefined) return '-';
    const s = String(v).trim();
    return s === '' ? '-' : s;
  };
  // Converte yyyy-mm-dd → dd/mm/yyyy
  const fmtDate = v => {
    if (!v || v === '-') return '-';
    const s = String(v).trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  };
  const fe = n => new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(n||0);
  const today = new Date().toLocaleDateString('it-IT');

  // ── Calcoli ──
  // prezzoListino è già corretto nel database (agevolato se legge104 attiva)
  const pL = parseFloat(prezzo.prezzoListino || veicolo.prezzo || 0);
  const sc   = parseFloat(prezzo.sconto||0);
  const ipt  = parseFloat(prezzo.ipt||0);
  const accessoriCMList = servizi.accessoriCasaMadreList || [];
  const accCM = accessoriCMList.reduce((s,a) => s + (parseFloat(a.prezzo)||0), 0);
  const hasIVA = !!prezzo.hasIVA;
  const ivaImporto = hasIVA ? Math.round((pL - sc) * 0.22) : 0;
  const pP   = pL - sc + ipt + accCM;
  const perm = valorePermuteTot(prezzo);
  const cap  = parseFloat(prezzo.caparra||0);
  const fin  = parseFloat(prezzo.importoFinanziato||0);

  const safeParse = v => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

  const costiServizi = [
    servizi.lojack               &&['Localizzatore radiofrequenze LoJack con centrale operativa H24' + (servizi.lojackPacchetto ? ` - ${lojackDescrizione(servizi)}` : ''), safeParse(servizi.costoLojack)],
    servizi.blockshaft           &&['Blockshaft',            safeParse(servizi.costoBlockshaft)],
    (servizi.mawdy36||servizi.mawdy)  &&[`Ext Garanzia Mawdy ${servizi.garanziaEstesaMesi || 36} mesi`, safeParse(servizi.costoMawdy36||servizi.costoMawdy)],
    servizi.altroAntifurto && typeof servizi.altroAntifurto === 'string' &&[sv(servizi.altroAntifurto), safeParse(servizi.costoAltroAntifurto)],
    // Assicurazioni (nuovi campi)
    servizi.assPP                &&[`Assicurazione PP${servizi.durAssPP ? ` (${servizi.durAssPP} mesi)` : ''}${servizi.inclAssPP ? ` — ${servizi.inclAssPP}` : ''}`,  safeParse(servizi.costoAssPP)],
    servizi.assFIR               &&[`Assicurazione F.I.R.${servizi.durAssFIR ? ` (${servizi.durAssFIR} mesi)` : ''}${servizi.inclAssFIR ? ` — ${servizi.inclAssFIR}` : ''}`, safeParse(servizi.costoAssFIR)],
    servizi.assGAP               &&[`Assicurazione G.A.P.${servizi.durAssGAP ? ` (${servizi.durAssGAP} mesi)` : ''}${servizi.inclAssGAP ? ` — ${servizi.inclAssGAP}` : ''}`, safeParse(servizi.costoAssGAP)],
    // Garanzia estesa (nuovi campi)
    (servizi.garanziaEstesaMesi > 0) &&[`Garanzia Estesa ${servizi.garanziaEstesaMesi} mesi`, safeParse(servizi.costoGaranzia)],
    (servizi.serviceCare && servizi.serviceCareMesi > 0) &&[`Service Care Gruppo Autoscala ${servizi.serviceCareMesi} mesi`, safeParse(servizi.costoServiceCare)],
    servizi.gpl                  &&['GPL',                   safeParse(servizi.costoGpl)],
    servizi.n1                   &&['N1 — Trasformazione Autocarro', safeParse(servizi.costoN1)],
    servizi.kitAutocarro         &&['Kit Autocarro',          safeParse(servizi.costoKitAutocarro)],
    servizi.altroInstallazione && typeof servizi.altroInstallazione === 'string' &&[sv(servizi.altroInstallazione), safeParse(servizi.costoAltroInstallazione)],
    servizi.sensoriParcheggio    &&['Sensori Parcheggio',    safeParse(servizi.costoSensoriParcheggio)],
    servizi.retrocamera          &&['Retrocamera',           safeParse(servizi.costoRetrocamera)],
    servizi.bracciolo            &&['Bracciolo',             safeParse(servizi.costoBracciolo)],
    servizi.vetriPrivacy         &&['Vetri Privacy',         safeParse(servizi.costoVetriPrivacy)],
    servizi.altroAccessorio && typeof servizi.altroAccessorio === 'string' &&[sv(servizi.altroAccessorio), safeParse(servizi.costoAltroAccessorio)],
    servizi.tagliando            &&['Tagliando',             safeParse(servizi.costoTagliando)],
    servizi.packManutenzione     &&[etichettaPackManutenzione(servizi), safeParse(servizi.costoPackManutenzione)],
    servizi.revisione            &&['Revisione',             safeParse(servizi.costoRevisione)],
    servizi.secondaChiave        &&['2a Chiave',             safeParse(servizi.costoSecondaChiave)],
    // Carrozzeria senza costo = nota, non servizio "incluso"
    servizi.lavorazioniCarrozzeria && typeof servizi.lavorazioniCarrozzeria === 'string' && safeParse(servizi.costoLavorazioniCarrozzeria) > 0 &&['Lavorazioni carrozzeria', safeParse(servizi.costoLavorazioniCarrozzeria)],
  ].filter(Boolean);

  const totS  = costiServizi.reduce((t,[,c])=>t+c,0);
  const estinzioneCosto = prezzo.estinzioneAnticipata ? safeParse(prezzo.costoEstinzioneAnticipata) : 0;
  const speseLeasingCosto = prezzo.speseGestioneLeasing ? safeParse(prezzo.costoSpeseGestioneLeasing) : 0;
  const saldo = pP + totS + ivaImporto - perm - cap - fin + estinzioneCosto + speseLeasingCosto;

  // ── Carica logo ──
  let logoData=null, logoRW=0;
  try {
    const img=new Image(); img.crossOrigin='anonymous';
    await new Promise(r=>{img.onload=r;img.onerror=r;img.src='/logo-blue.png';});
    if(img.naturalWidth>0){
      const c=document.createElement('canvas');
      c.width=img.naturalWidth;c.height=img.naturalHeight;
      c.getContext('2d').drawImage(img,0,0);
      logoData=c.toDataURL('image/png');
      logoRW=11*(img.naturalWidth/img.naturalHeight);
    }
  } catch(e){}

  // ── Carica foto auto ──
  let fotoData=null, fotoRatio=16/9;
  try {
    if(veicolo.mainImg){
      const fi=new Image();fi.crossOrigin='anonymous';
      await new Promise(r=>{fi.onload=r;fi.onerror=r;fi.src=veicolo.mainImg;});
      if(fi.naturalWidth>0){
        const fc=document.createElement('canvas');
        fc.width=fi.naturalWidth;fc.height=fi.naturalHeight;
        fc.getContext('2d').drawImage(fi,0,0);
        fotoData=fc.toDataURL('image/jpeg',0.82);
        fotoRatio=fi.naturalWidth/fi.naturalHeight;
      }
    }
  } catch(e){}

  // ══ LAYOUT ENGINE ══════════════════════════════════════════════════════════
  // Ogni elemento ha altezza fissa. y avanza SEMPRE di altezza elemento.
  // Nessun calcolo relativo — tutto assoluto.

  const RH   = 5.0;  // altezza riga dati
  const SHH  = 6.0;  // altezza header sezione (sfondo)
  const STY  = 4.2;  // offset testo dentro header sezione
  const GAP  = 2.0;  // spazio tra sezioni

  let y = 0;

  // ── Sfondo bianco + bordi blu ──
  doc.setFillColor(...WHITE); doc.rect(0,0,W,H,'F');
  doc.setFillColor(...BLUE);  doc.rect(0,0,W,1.8,'F');
  doc.rect(0,H-1.8,W,1.8,'F');

  // ── Header ──
  y = 4;
  if(logoData) doc.addImage(logoData,'PNG',ML,y,logoRW,11);
  doc.setFontSize(17);doc.setFont('helvetica','bold');doc.setTextColor(...BLUE);
  doc.text('PREVENTIVO',W-MR,y+8,{align:'right'});
  doc.setFontSize(7);doc.setFont('helvetica','normal');doc.setTextColor(...DGRAY);
  doc.text('Gruppo Auto Scala Srl - Via Antiniana, 2/B - 80078 Pozzuoli (NA)',W-MR,y+12,{align:'right'});
  doc.text('N. '+String(Date.now()).slice(-6)+'   Data: '+today,W-MR,y+16,{align:'right'});
  doc.setDrawColor(...BLUE);doc.setLineWidth(0.4);
  doc.line(ML,y+18,W-MR,y+18);
  y += 21;

  // ── Blocco veicolo: foto destra + info sinistra ──
  const FW = 65;
  const FH = Math.min(Math.round(FW/fotoRatio), 38);
  const FX = W-MR-FW;
  const IW = FX - ML - 4;

  if(fotoData){
    doc.setDrawColor(...MGRAY);doc.setLineWidth(0.25);
    doc.rect(FX,y,FW,FH,'S');
    doc.addImage(fotoData,'JPEG',FX,y,FW,FH);
  }

  // Marca + Modello
  doc.setFontSize(12);doc.setFont('helvetica','bold');doc.setTextColor(...BLUE);
  const mmStr = sv(veicolo.marca)+' '+sv(veicolo.modello);
  const mmLines = doc.splitTextToSize(mmStr, IW);
  let iy = y+7;
  mmLines.forEach(l=>{doc.text(l,ML,iy);iy+=6;});

  // Campi veicolo (2 per riga)
  doc.setFontSize(7);
  const vCols = [
    ['Versione', veicolo.versione],['Targa', veicolo.targa || '—'],
    ['Telaio (VIN)', veicolo.vin || '—'],
    ['Km', new Intl.NumberFormat('it-IT').format(veicolo.km||0)],['Immat.', veicolo.registrazione || '—'],
    ['Colore', veicolo.colore||veicolo.coloreVeicolo||prezzo.coloreVeicolo||stockLiveV?.colore||''],['Tipo', veicolo.tipo],
    ['Consegna', fmtDate(prezzo.dataConsegna)],
    ['Venditore', prezzo.operatore],['Carburante', veicolo.carburante],
  ].filter(([,v])=>v&&v!=='-'&&v!=='0');

  const halfIW = (IW-4)/2;
  for(let i=0;i<vCols.length && iy < y+FH+1;i+=2){
    const [l1,v1]=vCols[i];
    const [l2,v2]=vCols[i+1]||['',''];
    doc.setFont('helvetica','bold');doc.setTextColor(...DGRAY);
    doc.text(sv(l1)+':',ML,iy);
    doc.setFont('helvetica','normal');doc.setTextColor(...BLACK);
    const v1t=doc.getTextWidth(sv(v1))>halfIW-16?doc.splitTextToSize(sv(v1),halfIW-16)[0]+'..':sv(v1);
    doc.text(v1t,ML+16,iy);
    if(l2){
      doc.setFont('helvetica','bold');doc.setTextColor(...DGRAY);
      doc.text(sv(l2)+':',ML+halfIW+2,iy);
      doc.setFont('helvetica','normal');doc.setTextColor(...BLACK);
      const v2t=doc.getTextWidth(sv(v2))>halfIW-14?doc.splitTextToSize(sv(v2),halfIW-14)[0]+'..':sv(v2);
      doc.text(v2t,ML+halfIW+16,iy);
    }
    iy+=4.5;
  }

  y = Math.max(y+FH, iy) + 1;

  // Accessori/Optionals
  if(veicolo.optionals && veicolo.optionals!=='-'){
    doc.setFontSize(5.5);doc.setFont('helvetica','bold');doc.setTextColor(...DGRAY);
    doc.text('Accessori:',ML,y+3.5);
    doc.setFont('helvetica','normal');doc.setTextColor(...BLACK);
    const optLines=doc.splitTextToSize(sv(veicolo.optionals),CW-20);
    optLines.slice(0,6).forEach((l,i)=>{doc.text(l,ML+20,y+3.5+i*3.2);});
    y += Math.min(optLines.length,6)*3.2 + 3;
  } else {
    y += 2;
  }

  // Opzioni pacchetti
  if(prezzo.opzioni && prezzo.opzioni!=='-'){
    doc.setFontSize(7);doc.setFont('helvetica','italic');doc.setTextColor(...DGRAY);
    const opLines=doc.splitTextToSize('Opzioni/Pacchetti: '+sv(prezzo.opzioni),CW);
    opLines.slice(0,2).forEach(l=>{doc.text(l,ML,y+3.5);y+=4;});
    y+=1;
  }

  doc.setDrawColor(...MGRAY);doc.setLineWidth(0.25);
  doc.line(ML,y,W-MR,y);
  y += GAP+1;

  // ══ FUNZIONI RIGA ══════════════════════════════════════════════════════════

  // Header sezione: sfondo pieno, testo bianco
  // y punta alla TOP del rettangolo header
  const SH = (title) => {
    doc.setFillColor(...BLUE);
    doc.rect(ML, y, CW, SHH, 'F');
    doc.setFontSize(7.5);doc.setFont('helvetica','bold');doc.setTextColor(...WHITE);
    doc.text(title, ML+3, y+STY);
    y += SHH; // y ora punta SUBITO DOPO l'header
  };

  let ri = 0;
  // Riga dati: y punta al BASELINE del testo
  // Il rettangolo sfondo inizia a y-RH+1 e ha altezza RH
  const DR = (label, value, bold, vc) => {
    if(ri%2===0){doc.setFillColor(...LGRAY);doc.rect(ML,y-RH+1,CW,RH,'F');}
    ri++;
    doc.setFontSize(7);doc.setFont('helvetica','normal');
    doc.setTextColor(...DGRAY);doc.text(sv(label),ML+2,y);
    doc.setFont('helvetica',bold?'bold':'normal');
    doc.setTextColor(...(vc||(bold?BLUE:BLACK)));
    const maxW=CW-52;const vs=sv(value);
    doc.text(doc.getTextWidth(vs)>maxW?doc.splitTextToSize(vs,maxW)[0]+'..':vs,ML+50,y);
    doc.setDrawColor(...MGRAY);doc.setLineWidth(0.1);
    doc.line(ML,y+1,W-MR,y+1);
    y+=RH;
  };

  const DR2 = (l1,v1,l2,v2) => {
    if(ri%2===0){doc.setFillColor(...LGRAY);doc.rect(ML,y-RH+1,CW,RH,'F');}
    ri++;
    const cx=W/2+1, hw=(cx-ML-26), hw2=(W-MR-cx-24);
    doc.setFontSize(7);doc.setFont('helvetica','normal');
    doc.setTextColor(...DGRAY);doc.text(sv(l1),ML+2,y);
    doc.setTextColor(...BLACK);
    doc.text(doc.getTextWidth(sv(v1))>hw?doc.splitTextToSize(sv(v1),hw)[0]+'..':sv(v1),ML+26,y);
    if(l2){
      doc.setTextColor(...DGRAY);doc.text(sv(l2),cx,y);
      doc.setTextColor(...BLACK);
      doc.text(doc.getTextWidth(sv(v2))>hw2?doc.splitTextToSize(sv(v2),hw2)[0]+'..':sv(v2),cx+24,y);
    }
    doc.setDrawColor(...MGRAY);doc.setLineWidth(0.1);
    doc.line(ML,y+1,W-MR,y+1);
    y+=RH;
  };

  const AR = (label, value, bold, vc) => {
    if(bold){doc.setFillColor(225,235,255);doc.rect(ML,y-RH+1,CW,RH,'F');}
    else if(ri%2===0){doc.setFillColor(...LGRAY);doc.rect(ML,y-RH+1,CW,RH,'F');}
    ri++;
    doc.setFontSize(bold?7.5:7);doc.setFont('helvetica',bold?'bold':'normal');
    doc.setTextColor(...DGRAY);
    const safeLabel = sv(label);
    const safeValue = sv(value);
    doc.text(safeLabel, ML+2, y);
    doc.setTextColor(...(vc||(bold?GREEN:BLACK)));
    doc.text(safeValue, W-MR-2, y, {align:'right'});
    doc.setDrawColor(...MGRAY);doc.setLineWidth(0.1);
    doc.line(ML,y+1,W-MR,y+1);
    y+=RH;
  };

  // ── CLIENTE ──
  ri=0; SH('DATI CLIENTE');
  y+=RH; // prima riga parte con padding
  const nc=sv(cliente.ragioneSociale||((cliente.nome||'')+' '+(cliente.cognome||'')).trim());
  DR2('Nome / Rag. Sociale',nc,'CF / P.IVA',cliente.codiceFiscale||cliente.piva||'');
  DR2('Nato a',(cliente.natoA||'')+(cliente.dataNascita?' il '+fmtDate(cliente.dataNascita):''),'Professione',cliente.professione||'');
  DR2('Indirizzo',cliente.indirizzo||'','Citta',(cliente.citta||'')+(cliente.provincia?' ('+cliente.provincia+')':'')+(cliente.cap?' '+cliente.cap:''));
  DR2('Cellulare',cliente.cellulare||'','Cell. 2',cliente.cellulare2||'');
  DR2('Email',cliente.email||'','PEC',cliente.pec||'');
  if(cliente.sdi) DR2('Cod. SDI',cliente.sdi,'Email 2',cliente.email2||'');
  y+=GAP;

  // ── ECONOMICO ──
  ri=0; SH('CONDIZIONI ECONOMICHE');
  y+=RH;
  AR('Prezzo Listino Chiavi in Mano',fe(pL));
  if(sc>0) AR('Sconto Discrezionale','- '+fe(sc));
  if(hasIVA) AR('IVA 22%','+ '+fe(ivaImporto));
  if(ipt>0) AR(labelIPT(veicolo?.tipo),'+ '+fe(ipt));
  if(accCM>0) AR('Optional Casa Madre','+ '+fe(accCM));
  if(costiServizi.length>0) AR('Totale Servizi e Accessori (escl. Optional Casa Madre)','+ '+fe(totS));
  if(estinzioneCosto>0) AR('Estinzione Anticipata Finanziamento','+ '+fe(estinzioneCosto));
  if(speseLeasingCosto>0) AR('Spese Gestione Leasing','+ '+fe(speseLeasingCosto));
  AR('Prezzo Concordato (vettura + servizi)',fe(pP + totS + ivaImporto),true);
  if(perm>0) {
    const extraP = (prezzo.permuteAggiuntive || []).filter(x => parseFloat(x.valore) > 0);
    if (!extraP.length) AR(prezzo.permutaRottamazione ? 'Valore Rottamazione' : 'Valore Permuta', '- '+fe(perm));
    else {
      const v1 = parseFloat(prezzo.permutaValore || 0);
      if (v1 > 0) AR(`${prezzo.permutaRottamazione ? 'Rottamazione' : 'Permuta'} 1${prezzo.permutaTarga ? ' ('+prezzo.permutaTarga+')' : ''}`, '- '+fe(v1));
      extraP.forEach((x, i) => AR(`Permuta ${i + 2}${x.targa ? ' ('+x.targa+')' : ''}`, '- '+fe(parseFloat(x.valore))));
    }
  }
  if(fin>0)  AR('Importo Finanziato',        '- '+fe(fin));
  if(cap>0)  AR('Caparra Confirmatoria',     '- '+fe(cap));
  AR('SALDO A DEBITO',fe(saldo),true,GREEN);
  if(prezzo.modalitaPagamento) DR('Modalita Pagamento',prezzo.modalitaPagamento);
  // Dettagli finanziamento — solo se showPDF !== false
  if(prezzo.hasFinanziamento && prezzo.finanziamentoShowPDF !== false) {
    if(prezzo.finanziariaSocieta)    DR('Societa Finanziaria', sv(prezzo.finanziariaSocieta));
    if(prezzo.finanziamentoAnticipo) DR('Anticipo',            fe(parseFloat(prezzo.finanziamentoAnticipo)));
    if(prezzo.finanziamentoNRate && prezzo.finanziamentoRata)
      DR('Piano Rate', sv(prezzo.finanziamentoNRate)+' rate da '+fe(parseFloat(prezzo.finanziamentoRata))+'/mese');
    if(prezzo.finanziamentoDurata)   DR('Durata Finanziamento', sv(prezzo.finanziamentoDurata)+' mesi');
    if(prezzo.finanziamentoMaxirata) DR('Maxirata',       fe(parseFloat(prezzo.finanziamentoMaxirata)));
    if(prezzo.tabellaFinanziamento)  DR('Tabella Finanziamento', sv(prezzo.tabellaFinanziamento));
    if(prezzo.speseIstruttoria)      DR('Spese di Istruttoria',  fe(parseFloat(prezzo.speseIstruttoria)));
  } else if(prezzo.hasFinanziamento && prezzo.finanziariaSocieta) {
    DR('Societa Finanziaria', sv(prezzo.finanziariaSocieta));
  }
  y+=GAP;

  // ── PERMUTA ──
  if(prezzo.hasPermuta){
    ri=0; SH('RITIRO VEICOLO USATO');
    y+=RH;
    DR2('Marca Usato',prezzo.permutaMarca||'','Modello',prezzo.permutaModello||'');
    DR2('Targa',prezzo.permutaTarga||'','Anno',prezzo.permutaAnno||'');
    DR2('Km',prezzo.permutaKm?new Intl.NumberFormat('it-IT').format(prezzo.permutaKm):'','Valore Permuta',fe(perm));
    y+=GAP;
  }

  // ── OPTIONAL CASA MADRE (specchietto a parte — solo veicoli nuovi) ──
  if(accessoriCMList.length > 0){
    ri=0; SH('OPTIONAL CASA MADRE');
    y+=RH;
    accessoriCMList.forEach(acc => {
      if(acc.nome) AR(acc.nome, fe(parseFloat(acc.prezzo)||0));
    });
    if(accCM > 0){
      AR('TOTALE OPTIONAL CASA MADRE', fe(accCM), true, GREEN);
    }
    y += GAP;
  }

  // ── SERVIZI E ACCESSORI ──
  if(costiServizi.length > 0){
    ri=0; SH('SERVIZI E ACCESSORI');
    y+=RH;
    costiServizi.forEach(([nome, costo]) => {
      AR(nome, costo > 0 ? fe(costo) : 'incluso', false, costo > 0 ? GREEN : DGRAY);
    });
    if(totS > 0){
      AR('TOTALE SERVIZI', fe(totS), true, GREEN);
    }
    y += GAP;
  }

  // ── INCASSI ──
  if(prezzo.incassi&&prezzo.incassi.some(i=>i.importo)){
    ri=0; SH('SCHEDA INCASSI');
    y+=RH;
    doc.setFontSize(6.5);doc.setFont('helvetica','bold');doc.setTextColor(...DGRAY);
    doc.text('DATA',ML+2,y-1);doc.text('MODALITA',ML+38,y-1);
    doc.text('IMPORTO',W-MR-2,y-1,{align:'right'});
    y+=1;
    prezzo.incassi.filter(i=>i.importo).forEach(inc=>{
      if(ri%2===0){doc.setFillColor(...LGRAY);doc.rect(ML,y-RH+1,CW,RH,'F');}
      ri++;
      doc.setFontSize(7);doc.setFont('helvetica','normal');
      doc.setTextColor(...BLACK);doc.text(sv(inc.data),ML+2,y);doc.text(sv(inc.modalita),ML+38,y);
      doc.setTextColor(...GREEN);doc.text(fe(inc.importo),W-MR-2,y,{align:'right'});
      doc.setDrawColor(...MGRAY);doc.line(ML,y+1,W-MR,y+1);
      y+=RH;
    });
    y+=GAP;
  }

  // ── NOTE ──
  if(servizi.note){
    SH('NOTE'); y+=RH;
    doc.setFontSize(7);doc.setFont('helvetica','normal');doc.setTextColor(...BLACK);
    doc.splitTextToSize(sv(servizi.note),CW-4).slice(0,3).forEach(l=>{doc.text(l,ML+2,y);y+=4;});
    y+=GAP;
  }

  // ── IL TUO CONSULENTE (recapiti) ──
  const recCons = recapitiConsulente(prezzo.operatore);
  if (recCons) {
    if (y + 52 > H - 8) { doc.addPage(); y = 15; }
    doc.setFillColor(...LGRAY); doc.rect(ML, y, CW, 11, 'F');
    doc.setFontSize(6.5); doc.setFont('helvetica','bold'); doc.setTextColor(...DGRAY);
    doc.text('IL TUO CONSULENTE', ML + 2, y + 4.2);
    doc.setFontSize(8); doc.setFont('helvetica','bold'); doc.setTextColor(...BLACK);
    doc.text(recCons.nome, ML + 2, y + 8.6);
    doc.setFont('helvetica','normal');
    doc.text(`Cell. ${recCons.cell}`, ML + CW * 0.38, y + 8.6);
    doc.text(recCons.email, W - MR - 2, y + 8.6, { align: 'right' });
    y += 14;
  }

  // ── FIRME ──
  y+=2;
  doc.setDrawColor(...MGRAY);doc.setLineWidth(0.25);
  doc.rect(ML,y,CW,32,'S');
  doc.setFontSize(7);doc.setFont('helvetica','normal');doc.setTextColor(...DGRAY);
  const fc1=ML+4, fc2=W/2-12, fc3=W-MR-56;
  doc.text('Il Cliente',fc1+20,y+5,{align:'center'});
  doc.text('Data',fc2+12,y+5,{align:'center'});
  doc.text('Gruppo Auto Scala Srl',fc3+26,y+5,{align:'center'});
  doc.setDrawColor(...BLACK);doc.setLineWidth(0.25);
  doc.line(fc1,y+24,fc1+44,y+24);
  doc.line(fc2,y+24,fc2+26,y+24);
  doc.line(fc3,y+24,fc3+52,y+24);
  y+=34;

  doc.setFontSize(5.5);doc.setTextColor(...MGRAY);
  doc.splitTextToSize('La garanzia del veicolo usato vale solo se i tagliandi sono effettuati presso: Gruppo Auto Scala Srl, Via Antiniana 2/B, 80078 Pozzuoli (NA).',CW)
    .forEach(l=>{doc.text(l,ML,y);y+=3.5;});

  const nf='preventivo_'+nc.replace(/\s+/g,'_')+'_'+sv(veicolo.marca)+'_'+sv(veicolo.modello)+'_'+today.replace(/\//g,'-')+'.pdf';
  doc.save(nf);
}

// Versione che restituisce blob invece di salvare — usata per la preview
export async function generatePDFBlob(veicolo, cliente, prezzo, servizi) {
  if (!window.jspdf) {
    await new Promise((res, rej) => {
      const sc = document.createElement('script');
      sc.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      sc.onload = res; sc.onerror = rej;
      document.head.appendChild(sc);
    });
  }
  const origSave = window.jspdf.jsPDF.prototype.save;
  let blob = null;
  window.jspdf.jsPDF.prototype.save = function() {
    // Cattura il blob senza scaricare
    blob = this.output('blob');
  };
  try {
    await generatePDF(veicolo, cliente, prezzo, servizi);
  } finally {
    window.jspdf.jsPDF.prototype.save = origSave;
  }
  return blob;
}

// ─── MODAL PRINCIPALE ─────────────────────────────────────────────────────────
// preventivoEsistente: se passato, il modal è in modalità MODIFICA
export default function PreventivoModal({ car, user, db, onClose, onSaved, preventivoEsistente, clientePrecompilato, preventivi }) {

  // Blocca rotellina su input numerici
  useEffect(() => {
    const handler = (e) => { if (e.target.type === 'number') e.preventDefault(); };
    document.addEventListener('wheel', handler, { passive: false });
    return () => document.removeEventListener('wheel', handler);
  }, []);
  const isMobile = window.innerWidth < 640;
  const isDark = (() => { try { return (localStorage.getItem('radar_theme') || 'dark') === 'dark'; } catch { return true; } })();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const isEdit = !!preventivoEsistente;

  // In modifica: precompila con i dati esistenti; in creazione: dati vuoti (o cliente precompilato)
  const [veicoloData, setVeicoloData] = useState(
    isEdit ? { ...preventivoEsistente.veicolo, ...car } : { ...car, prezzoListino: (((user?.ruolo||'').toLowerCase() === 'partner') && car.prezzoPartner > 0) ? car.prezzoPartner : (car.prezzo || 0) }
  );
  const _isPartner = (user?.ruolo||'').toLowerCase() === 'partner';
  // Antonio Moio: ha ruolo "partner" ma è un consulente interno — i suoi
  // preventivi devono restare B2B di default, ma con il cliente vero
  // digitato da lui, MAI il fisso "Overcar" (quello resta solo per il
  // partner Overcar originale, che questo ruolo "partner" era pensato per).
  const _isPartnerOvercar = _isPartner && `${user?.nome||''} ${user?.cognome||''}`.toLowerCase().trim() !== 'antonio moio';

  const [clienteData, setClienteData] = useState(
    isEdit ? (preventivoEsistente.cliente || {}) :
    _isPartnerOvercar ? { ragioneSociale: 'Overcar', b2b: true, isPartner: true, email: user?.email || '', piva: 'OVERCAR', codiceFiscale: '' } :
    _isPartner ? { b2b: true, email: user?.email || '' } : // Antonio Moio: B2B di default, cliente vero da compilare
    (clientePrecompilato || {})
  );
  const [prezzoData, setPrezzoData] = useState(
    isEdit ? (preventivoEsistente.prezzo || {
      prezzoListino: _isPartner && car.prezzoPartner > 0 ? car.prezzoPartner : (car.prezzo || 0),
      incassi: [{ data: '', modalita: '', importo: '' }],
      operatore: user?.nome ? `${user.nome} ${user.cognome}` : '',
    }) : {
      prezzoListino: _isPartner && car.prezzoPartner > 0 ? car.prezzoPartner : (car.prezzo || 0),
      incassi: [{ data: '', modalita: '', importo: '' }],
      operatore: user?.nome ? `${user.nome} ${user.cognome}` : '',
    }
  );
  const [serviziData, setServiziData] = useState(
    isEdit ? (preventivoEsistente.servizi || {}) : {}
  );

  const updateVeicolo = (k, v) => {
    setVeicoloData(p => ({ ...p, [k]: v }));
    // Se off stock e si cambia il prezzo, aggiorna anche prezzoListino
    if (k === 'prezzo' && car.offStock) {
      setPrezzoData(p => ({ ...p, prezzoListino: parseFloat(v)||0 }));
    }
  };
  const updateCliente = (k, v) => setClienteData(p => ({ ...p, [k]: v }));
  const updatePrezzo  = (k, v) => setPrezzoData(p => ({ ...p, [k]: v }));
  const updateServizi = (k, v) => setServiziData(p => ({ ...p, [k]: v }));

  const stepTitles = ['Dati Veicolo', 'Prezzo e Servizi', 'Dati Cliente'];

  const renderStep = () => {
    switch(step) {
      case 0: return <Step1Veicolo data={{ ...veicoloData, prezzoListino: prezzoData.prezzoListino }} onChange={(k, v) => { if (k === 'prezzoListino') updatePrezzo(k, v); else updateVeicolo(k, v); }} user={user} />;
      case 1: return <>
        <Step3Prezzo data={prezzoData} onChange={updatePrezzo} veicolo={car} legge104={veicoloData.legge104} user={user}
          totaleServizi={Object.entries(serviziData).filter(([k]) => k.startsWith('costo')).reduce((t,[,v]) => t+(parseFloat(v)||0), 0)}
          accessoriCMTot={(serviziData.accessoriCasaMadreList || []).reduce((s,a) => s + (parseFloat(a.prezzo)||0), 0)} />
        <div style={{ marginTop:'16px', borderTop:`1px solid ${isDark?'#1e2230':'#e2e8f0'}`, paddingTop:'16px' }}>
          <Step4Servizi data={serviziData} onChange={updateServizi} veicolo={car} />
        </div>
        {/* RIEPILOGO LIVE rimosso — integrato in Step3Prezzo */}
      </>;
      case 2: return <Step2Cliente data={clienteData} onChange={updateCliente} preventiviEsistenti={preventivi} isDark={isDark} />;
      default: return null;
    }
  };

  const handleSaveAndPDF = async () => {
    // Validazione campi obbligatori cliente
    const cli = clienteData;
    const isAz = !!(cli.ragioneSociale?.trim());
    const campiMancanti = [];
    if (!isAz && !cli.nome?.trim()) campiMancanti.push('Nome');
    if (!isAz && !cli.cognome?.trim()) campiMancanti.push('Cognome');
    if (!cli.telefono?.trim() && !cli.cellulare?.trim()) campiMancanti.push('Telefono / Cellulare');
    if (!cli.email?.trim()) campiMancanti.push('Email');
    if (!cli.citta?.trim()) campiMancanti.push('Città');
    if (!cli.cap?.trim()) campiMancanti.push('CAP');
    if (!cli.b2b && !cli.provenienza?.trim()) campiMancanti.push('Provenienza');
    if (campiMancanti.length > 0) {
      alert('Campi obbligatori mancanti:\n• ' + campiMancanti.join('\n• '));
      setStep(2); // porta allo step cliente
      setSaving(false);
      return;
    }
    setSaving(true);
    try {
      // Aggiorna la cache stock prima di salvare — sempre fresca, non solo
      // se già popolata in questa sessione (evita colore/dati congelati).
      try {
        const dbLive = getDatabase();
        const snapStock = await get(ref(dbLive, 'stock_data'));
        if (snapStock.exists() && snapStock.val()?.vehicles) {
          window._radarStockCache = snapStock.val();
        }
      } catch (e) { /* offline: si userà l'eventuale cache già presente */ }

      const clean = (o, depth=0) => {
        if (depth > 5) return null; // tronca riferimenti profondi/circolari
        if (o === null || o === undefined) return '';
        if (typeof o === 'boolean') return o;
        if (typeof o === 'number') return o;
        if (Array.isArray(o)) return o.map(x=>clean(x,depth+1));
        if (typeof o === "object") {
          const r = {};
          for (const [k,v] of Object.entries(o)) {
            // Escludi campi che causano riferimenti circolari
            if (k === 'ultimoPreventivo' || k === 'preventiviPrecedenti') continue;
            r[k] = clean(v, depth+1);
          }
          return r;
        }
        return o;
      };
      // In modifica mantieni id e data originale, aggiorna dataModifica
      const idPreventivo = isEdit ? preventivoEsistente.id : Date.now().toString();
      const prezzoBase = parseFloat(prezzoData.prezzoListino || car.prezzo || 0);
      const prezzoListinoFinale = veicoloData.legge104
        ? Math.round((prezzoBase / 1.22) * 1.04)
        : prezzoBase;

      const isPartner = (user?.ruolo||'').toLowerCase() === 'partner';
      const isPartnerOvercar = isPartner && `${user?.nome||''} ${user?.cognome||''}`.toLowerCase().trim() !== 'antonio moio';

      const preventivo = clean({
        id: idPreventivo,
        dataCreazione: isEdit ? preventivoEsistente.dataCreazione : new Date().toISOString(),
        dataModifica: new Date().toISOString(),
        venditoreUid: isEdit ? preventivoEsistente.venditoreUid : (user.uid || user.email || (user.nome+"."+user.cognome)),
        venditoreNome: prezzoData.operatore || (isEdit ? preventivoEsistente.venditoreNome : (user.nome+" "+user.cognome)),
        venditoreRuolo: isEdit ? preventivoEsistente.venditoreRuolo : ((user.ruolo||user.Ruolo||'').toLowerCase().trim()),
        veicolo: { id: car.id||veicoloData.id||"", marca: veicoloData.marca||car.marca||"", modello: veicoloData.modello||car.modello||"", targa: veicoloData.targa||car.targa||"", vin: veicoloData.vin||car.vin||"", registrazione: veicoloData.registrazione||veicoloData.anno||car.registrazione||"", km: parseFloat(veicoloData.km||car.km)||0, carburante: veicoloData.carburante||car.carburante||"",
          // Partner usa prezzoPartner se disponibile
          prezzo: isPartner && (car.prezzoPartner||0) > 0 ? car.prezzoPartner : (veicoloData.prezzo||car.prezzo||0),
          tipo: veicoloData.tipo||car.tipo||"", versione: veicoloData.versione||car.versione||"", coloreVeicolo: (() => {
            const sc = window._radarStockCache?.vehicles || [];
            const norm2 = s2 => (s2 || '').toString().trim().toUpperCase().replace(/\s+/g, '');
            const idCerca = car.id || veicoloData.id;
            const targaCerca = car.targa || veicoloData.targa;
            const vinCerca = car.vin || veicoloData.vin;
            const sv3 = sc.find(s => (idCerca && s.id === idCerca) || (targaCerca && norm2(s.targa) === norm2(targaCerca)) || (vinCerca && norm2(s.vin) === norm2(vinCerca)));
            return sv3?.colore || veicoloData.coloreVeicolo || car.coloreVeicolo || car.colore || "";
          })(), mainImg: veicoloData.mainImg||car.mainImg||"", offStock: car.offStock||false },
        // Partner — cliente Overcar B2B fisso, TRANNE per Antonio Moio (che
        // è un consulente interno con ruolo partner: resta B2B, ma con il
        // cliente vero digitato da lui, non un nome fisso).
        cliente: isPartnerOvercar ? {
          ...clienteData,
          ragioneSociale: 'Overcar',
          b2b: true,
          isPartner: true,
        } : isPartner ? { ...clienteData, b2b: true } : clienteData,
        prezzo: { ...prezzoData,
          prezzoListino: isPartner && (car.prezzoPartner||0) > 0 ? car.prezzoPartner : prezzoListinoFinale,
          legge104: veicoloData.legge104 || false
        },
        servizi: serviziData,
        stato: "bozza",
      });
      await set(ref(db, `preventivi/${preventivo.id}`), preventivo);
      // Forza il refresh immediato della lista in App.jsx (che ora usa cache
      // con polling ogni 60s invece di onValue) — così l'utente vede subito
      // il proprio preventivo appena salvato, senza aspettare il ciclo.
      try { window._forzaRefreshContrattiPreventivi?.(); } catch (e) {}

      // Notifica LeadSpark in background
      try {
        fetch('http://localhost:3002/notify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'preventivo', preventivo }),
        }).catch(() => {});
      } catch(e) {}
      await generatePDF(preventivo.veicolo, clienteData, preventivo.prezzo, serviziData);
      // LeadSpark — Trattativa Aperta
      try {
        if (typeof window.sendToLeadSpark === 'function') {
          // Calcola amount_offer correttamente: listino - sconto + servizi
          const _pL  = parseFloat(prezzoData.prezzoListino || car.prezzo || 0);
          const _sc  = parseFloat(prezzoData.sconto || 0);
          const _ipt = parseFloat(prezzoData.ipt || 0);
          const _accCM = (serviziData?.accessoriCasaMadreList || []).reduce((s,a)=>s+(parseFloat(a.prezzo)||0),0);
          const _totS = Object.entries(serviziData||{}).filter(([k])=>k.startsWith('costo')).reduce((t,[,v])=>t+(parseFloat(v)||0),0);
          const _amountOffer = Math.max(0, _pL + _ipt + _accCM + _totS - _sc);

          window.sendToLeadSpark({
            id: preventivo.id,
            preventivoId: preventivo.id,
            fonte: prezzoData.fonte || '',
            dataCreazione: preventivo.dataCreazione || new Date().toISOString(),
            venditoreNome: `${user.nome} ${user.cognome}`,
            cliente: clienteData,
            veicolo: { ...veicoloData, marca: car.marca, modello: car.modello, targa: car.targa, vin: car.vin, versione: car.versione, tipo: car.tipo, km: car.km, sede: car.sede },
            prezzo: {
              ...prezzoData,
              _amountOffer,  // importo pre-calcolato per buildLSPayload
            },
            servizi: serviziData,
            note: '',
          }, 'A');
        }
      } catch(lsErr) { console.warn('LeadSpark preventivo:', lsErr.message); }
      onSaved && onSaved(preventivo);
      onClose();
    } catch (err) {
      console.error(err);
      alert('Errore durante il salvataggio: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 2000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: isDark ? '#141722' : '#ffffff', borderRadius: isMobile ? '24px 24px 0 0' : '24px', border: `1px solid ${isDark ? '#1e2230' : '#e2e8f0'}`, width: '100%', maxWidth: isMobile ? '100%' : '680px', maxHeight: isMobile ? '96vh' : '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* HEADER */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${isDark ? '#1e2230' : '#e2e8f0'}`, flexShrink: 0 }}>
          {isMobile && <div style={{ width: '36px', height: '4px', background: isDark ? '#2d334a' : '#cbd5e1', borderRadius: '2px', margin: '0 auto 16px' }} />}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '9px', color: isEdit ? '#f59e0b' : '#3b82f6', fontWeight: '900', letterSpacing: '2px', marginBottom: '4px' }}>
                {isEdit ? 'MODIFICA PREVENTIVO' : 'NUOVO PREVENTIVO'}
              </div>
              <div style={{ fontSize: '16px', fontWeight: '900', color: isDark ? '#fff' : '#0f172a' }}>{car.marca} {car.modello}</div>
              <div style={{ fontSize: '10px', color: isDark ? '#64748b' : '#64748b', marginTop: '2px' }}>{stepTitles[step]}</div>
            </div>
            <button onClick={onClose} style={{ background: isDark ? '#1e2230' : '#f1f5f9', border: 'none', color: isDark ? '#fff' : '#0f172a', borderRadius: '10px', padding: '8px 14px', cursor: 'pointer', fontWeight: '900', fontSize: '18px', lineHeight: 1, flexShrink: 0 }}>×</button>
          </div>
          <StepBar step={step} isMobile={isMobile} isDark={isDark} />
        </div>

        {/* CONTENUTO SCROLLABILE */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px', background: isDark ? '#141722' : '#ffffff' }}>
          {renderStep()}
        </div>

        {/* FOOTER */}
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${isDark ? '#1e2230' : '#e2e8f0'}`, display: 'flex', justifyContent: 'space-between', gap: '10px', flexShrink: 0, background: isDark ? '#141722' : '#f8fafc' }}>
          <button onClick={() => step > 0 ? setStep(s => s - 1) : onClose()} style={{ background: isDark ? '#1e2230' : '#f1f5f9', border: `1px solid ${isDark ? '#2d334a' : '#e2e8f0'}`, borderRadius: '12px', padding: '12px 20px', color: isDark ? '#fff' : '#0f172a', cursor: 'pointer', fontWeight: '800', fontSize: '11px' }}>
            {step === 0 ? 'Annulla' : '← Indietro'}
          </button>
          {step < 2 ? (
            <button onClick={() => setStep(s => s + 1)} style={{ background: '#3b82f6', border: 'none', borderRadius: '12px', padding: '12px 24px', color: '#fff', cursor: 'pointer', fontWeight: '900', fontSize: '11px' }}>
              Avanti →
            </button>
          ) : (
            <button onClick={handleSaveAndPDF} disabled={saving} style={{ background: saving ? '#2d334a' : (isEdit ? '#f59e0b' : '#10b981'), border: 'none', borderRadius: '12px', padding: '12px 24px', color: '#fff', cursor: saving ? 'default' : 'pointer', fontWeight: '900', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {saving ? '⏳ Salvataggio...' : (isEdit ? '💾 Aggiorna e Scarica PDF' : '📄 Salva e Scarica PDF')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
